/**
 * StoryGeneratorModal - Générateur d'histoires thaï interactives
 * Fonctionnalités: génération IA, quiz, test prononciation phrase par phrase,
 * traduction naturelle fidèle, support d'apprentissage "Manee t'explique"
 */
import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Volume2, 
  BookText, 
  Wand2, 
  Loader2, 
  Play, 
  Pause, 
  Mic, 
  MicOff, 
  X, 
  User, 
  Gauge, 
  GraduationCap, 
  Edit3, 
  CheckCircle2, 
  MessageCircle, 
  HelpCircle, 
  Target, 
  Check,
  Trophy,
  Sparkles,
  BookOpen,
  ChevronDown,
  ChevronUp,
  Layers,
  ChevronRight,
  Plus,
  RefreshCw,
  Users,
  Lightbulb
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import ManeeChat from "@/components/ManeeChat";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// Log user activity for progress tracking
const logActivity = async (activityType, word = null, details = null) => {
  try {
    await axios.post(`${API}/progress/log`, {
      activity_type: activityType,
      word: word,
      details: details
    });
  } catch (err) {
    console.error("Error logging activity:", err);
  }
};

// ============================================================
// PROMPT 3D: VALIDATION STRICTE DES SORTIES IA
// ============================================================

// Patterns de clés JSON techniques qui ne doivent JAMAIS être affichées
const JSON_KEY_PATTERNS = [
  /"street_notes"\s*:/,
  /"new_words"\s*:/,
  /"story_thai"\s*:/,
  /"story_french"\s*:/,
  /"grammar"\s*:/,
  /"culture"\s*:/,
  /"translation"\s*:/,
  /"key_words"\s*:/,
  /"vocabulary"\s*:/,
  /"exercises"\s*:/,
  /"remember"\s*:/,
  /"error_to_avoid"\s*:/,
  /"brief"\s*:/,
  /"detailed"\s*:/,
  /"confusions"\s*:/,
  /"tones"\s*:/,
  /^\s*\{[\s\S]*"[a-z_]+":\s*/,
  /^\s*\[[\s\S]*\{[\s\S]*"[a-z_]+":/
];

// Patterns de romanisation interdits
const ROMANIZATION_PATTERNS = [
  /\bsawasdee\b/i,
  /\bmai pen rai\b/i,
  /\bkha\b/i,
  /\bkhrap\b/i,
  /\bphom\b/i,
  /\bchan\b/i,
  /\byak\b/i,
  /\bsom tam\b/i,
  /\bkhob khun\b/i,
  /\bkob kun\b/i,
  /\bsawatdee\b/i,
  /\bkrap\b/i
];

// Phrases fallback génériques interdites
const FORBIDDEN_FALLBACK_PHRASES = [
  "Aucune confusion importante à signaler ici",
  "Analyse grammaticale non disponible",
  "Pas d'erreur particulière",
  "Ces expressions sont courantes",
  "Cette phrase est utile dans la vie quotidienne",
  "Support non disponible",
  "non disponible pour le moment"
];

// ============================================================
// PROMPT 3E: VALIDATION ANCRÉE DES BLOCS PÉDAGOGIQUES
// ============================================================

// Sujets thaïs explicites - Si présents, on NE PEUT PAS parler d'omission du sujet
const EXPLICIT_SUBJECTS = [
  'คุณ', 'ผม', 'ฉัน', 'เขา', 'เรา', 'หนู', 'พี่', 'น้อง', 'เธอ', 'มัน', 'ท่าน'
];

// Phrases vagues interdites sans citation thaïe exacte
const VAGUE_PHRASES_WITHOUT_CITATION = [
  /^cette phrase\s/i,
  /^ces expressions?\s/i,
  /^cela\s/i,
  /^ici\s/i,
  /^dans cette scène\s/i,
  /^l'utilisation de\s/i,
  /^cette formule\s/i,
  /la phrase permet/i,
  /cette structure permet/i
];

// Patterns de contradiction "omission du sujet"
const SUBJECT_OMISSION_CLAIMS = [
  /le sujet est omis/i,
  /la phrase omet le sujet/i,
  /omet le sujet/i,
  /omission du sujet/i,
  /sujet omis/i,
  /sujet est implicite/i,
  /sans sujet explicite/i
];

/**
 * PROMPT 3E: Vérifie si un texte contient une citation thaïe exacte
 * Une explication doit citer au moins un mot/phrase thaï
 */
const containsThaiCitation = (text) => {
  if (!text || typeof text !== 'string') return false;
  const thaiPattern = /[\u0E00-\u0E7F]{2,}/g; // Au moins 2 caractères thaïs consécutifs
  return thaiPattern.test(text);
};

/**
 * PROMPT 3E: Vérifie si une explication est trop vague (sans citation)
 */
const isVagueWithoutCitation = (text) => {
  if (!text || typeof text !== 'string') return false;
  
  // Si le texte contient une citation thaïe, ce n'est pas trop vague
  if (containsThaiCitation(text)) return false;
  
  // Vérifier si le texte commence par une phrase vague
  return VAGUE_PHRASES_WITHOUT_CITATION.some(pattern => pattern.test(text.trim()));
};

/**
 * PROMPT 3E: Vérifie si une explication parle d'omission du sujet
 */
const claimsSubjectOmission = (text) => {
  if (!text || typeof text !== 'string') return false;
  return SUBJECT_OMISSION_CLAIMS.some(pattern => pattern.test(text));
};

/**
 * PROMPT 3E: Vérifie si une phrase thaïe contient un sujet explicite
 */
const hasExplicitSubject = (thaiText) => {
  if (!thaiText || typeof thaiText !== 'string') return false;
  return EXPLICIT_SUBJECTS.some(subject => thaiText.includes(subject));
};

/**
 * PROMPT 3E: Valide un bloc pédagogique avec vérification d'ancrage
 * Vérifie que l'explication correspond réellement à la scène thaïe
 * @param {string} explanation - L'explication pédagogique
 * @param {string} storyThai - Le texte thaï de la scène actuelle
 * @param {string} blockName - Nom du bloc pour le logging
 * @returns {string|null} - L'explication validée ou null si invalide
 */
const validateAnchoredBlock = (explanation, storyThai, blockName = 'bloc') => {
  if (!explanation || typeof explanation !== 'string') return null;
  
  const trimmed = explanation.trim();
  if (trimmed.length === 0) return null;
  
  // Vérifications de base PROMPT 3D
  if (containsRawJSON(trimmed)) {
    console.warn(`[PROMPT 3E] JSON brut dans ${blockName}`);
    return null;
  }
  
  if (containsRomanization(trimmed)) {
    console.warn(`[PROMPT 3E] Romanisation dans ${blockName}`);
    return null;
  }
  
  if (isForbiddenFallback(trimmed)) {
    console.warn(`[PROMPT 3E] Fallback générique dans ${blockName}`);
    return null;
  }
  
  // PROMPT 3E: Vérification d'ancrage
  
  // 1. Vérifier que le bloc n'est pas trop vague
  if (isVagueWithoutCitation(trimmed)) {
    console.warn(`[PROMPT 3E] Explication vague sans citation thaïe dans ${blockName}`);
    return null;
  }
  
  // 2. RÈGLE CRITIQUE: Vérifier la contradiction "omission du sujet"
  if (claimsSubjectOmission(trimmed) && storyThai && hasExplicitSubject(storyThai)) {
    console.warn(`[PROMPT 3E] CONTRADICTION: Parle d'omission du sujet mais la scène contient un sujet explicite (${blockName})`);
    return null;
  }
  
  // 3. Pour les blocs Culture/Usage, vérifier qu'ils citent du thaï
  if (['culture', 'usage', 'culturalContext', 'realLifeUsage'].some(k => blockName.toLowerCase().includes(k))) {
    if (!containsThaiCitation(trimmed)) {
      console.warn(`[PROMPT 3E] Bloc ${blockName} sans citation thaïe exacte`);
      return null;
    }
  }
  
  return trimmed;
};

// PROMPT 3D BIS: Patterns de refus IA qui ne doivent JAMAIS apparaître dans une scène
const AI_REFUSAL_PATTERNS = [
  /je suis désolé/i,
  /je ne suis pas en mesure/i,
  /règles de contenu/i,
  /restrictions/i,
  /je ne peux pas/i,
  /impossible de/i,
  /je m'excuse/i,
  /malheureusement/i,
  /en tant qu'assistant/i,
  /en tant qu'IA/i,
  /je ne suis pas autorisé/i,
  /politique de contenu/i,
  /guidelines/i,
  /content policy/i,
  /I cannot/i,
  /I'm sorry/i,
  /I apologize/i,
  /unable to/i,
  /cannot assist/i
];

/**
 * PROMPT 3D BIS: Vérifie si un texte est un refus IA
 */
const isAIRefusal = (text) => {
  if (!text || typeof text !== 'string') return false;
  return AI_REFUSAL_PATTERNS.some(pattern => pattern.test(text));
};

/**
 * PROMPT 3D BIS: Vérifie si un texte contient du thaï valide
 * Une scène thaïe doit contenir une proportion significative de caractères thaïs
 */
const containsValidThai = (text) => {
  if (!text || typeof text !== 'string') return false;
  const thaiPattern = /[\u0E00-\u0E7F]/g;
  const thaiChars = (text.match(thaiPattern) || []).length;
  // Une scène thaïe valide doit avoir au moins 20% de caractères thaïs
  const totalChars = text.replace(/\s/g, '').length;
  return totalChars > 0 && (thaiChars / totalChars) >= 0.2;
};

/**
 * PROMPT 3D: Vérifie si un texte contient du JSON brut
 */
const containsRawJSON = (text) => {
  if (!text || typeof text !== 'string') return false;
  return JSON_KEY_PATTERNS.some(pattern => pattern.test(text));
};

/**
 * PROMPT 3D: Vérifie si un texte contient de la romanisation
 */
const containsRomanization = (text) => {
  if (!text || typeof text !== 'string') return false;
  return ROMANIZATION_PATTERNS.some(pattern => pattern.test(text));
};

/**
 * PROMPT 3D: Vérifie si un texte est une phrase fallback générique
 */
const isForbiddenFallback = (text) => {
  if (!text || typeof text !== 'string') return false;
  return FORBIDDEN_FALLBACK_PHRASES.some(phrase => 
    text.toLowerCase().includes(phrase.toLowerCase())
  );
};

/**
 * PROMPT 3D: Valide un bloc pédagogique avant affichage
 * Retourne null si le bloc ne doit pas être affiché
 */
const validatePedagogicalBlock = (text, blockName = 'bloc') => {
  if (!text || typeof text !== 'string') return null;
  
  const trimmed = text.trim();
  if (trimmed.length === 0) return null;
  
  // Vérifications PROMPT 3D
  if (containsRawJSON(trimmed)) {
    console.warn(`[PROMPT 3D] JSON brut détecté dans ${blockName}, bloc masqué`);
    return null;
  }
  
  if (containsRomanization(trimmed)) {
    console.warn(`[PROMPT 3D] Romanisation détectée dans ${blockName}, bloc masqué`);
    return null;
  }
  
  if (isForbiddenFallback(trimmed)) {
    console.warn(`[PROMPT 3D] Fallback générique détecté dans ${blockName}, bloc masqué`);
    return null;
  }
  
  return trimmed;
};

// ============================================================
// PROMPT 3F: STRUCTURE STABLE DU SUPPORT D'APPRENTISSAGE
// ============================================================

/**
 * PROMPT 3F: Extrait les phrases thaïes de la scène pour les fallbacks
 */
const extractThaiPhrases = (storyThai) => {
  if (!storyThai) return [];
  const lines = storyThai.split('\n').filter(line => line.trim());
  const phrases = [];
  
  for (const line of lines) {
    // Enlever les labels (ผม:, Moi:, พนักงาน:, etc.)
    const cleanedLine = line.replace(/^[A-Za-zก-๛]+:\s*/, '').trim();
    if (cleanedLine && containsThaiCitation(cleanedLine)) {
      phrases.push(cleanedLine);
    }
  }
  return phrases;
};

/**
 * PROMPT 3F: Génère un bloc fallback fiable à partir d'une phrase de la scène
 */
/**
 * PROMPT 3G: Extrait un mot clé significatif d'une phrase thaïe avec son sens
 * Retourne un objet { word, meaning } ou null
 */
const extractKeyWordFromPhrase = (phrase) => {
  if (!phrase) return null;
  
  // Dictionnaire de mots courants avec leur sens français
  const commonWords = {
    'ครับ': 'particule de politesse masculine',
    'ค่ะ': 'particule de politesse féminine',
    'ขอ': 'demander poliment',
    'ได้': 'pouvoir / obtenir',
    'ไหม': 'particule interrogative',
    'นะ': 'particule d\'adoucissement',
    'หน่อย': 'un peu (adoucit une demande)',
    'ไม่': 'négation',
    'เอา': 'prendre / vouloir',
    'อะไร': 'quoi / quelque chose',
    'เท่าไหร่': 'combien',
    'กี่': 'combien de',
    'บาท': 'baht (monnaie)',
    'ที่': 'qui / que / où',
    'นี้': 'ce / cette / ceci',
    'กิน': 'manger',
    'ดื่ม': 'boire',
    'ไป': 'aller',
    'มา': 'venir',
    'อยู่': 'être situé / rester',
    'มี': 'avoir / il y a',
    'เป็น': 'être',
    'ทำ': 'faire',
    'พูด': 'parler',
    'ฟัง': 'écouter',
    'ดู': 'regarder',
    'รอ': 'attendre',
    'ช่วย': 'aider',
    'ขอบคุณ': 'merci',
    'สวัสดี': 'bonjour',
    'ไม่เป็นไร': 'ce n\'est rien / pas grave',
    'เดี๋ยว': 'un moment / bientôt',
    'แล้ว': 'déjà / ensuite',
    'ก่อน': 'avant / d\'abord',
    'อีก': 'encore / de plus',
    'ด้วย': 'aussi / avec',
    'แต่': 'mais',
    'ถ้า': 'si',
    'เพราะ': 'parce que',
    'แพง': 'cher',
    'ถูก': 'bon marché / correct',
    'ร้อน': 'chaud',
    'เย็น': 'froid / frais',
    'อร่อย': 'délicieux',
    'สวย': 'beau / belle',
    'ดี': 'bien / bon',
    'เก่ง': 'doué / compétent',
    'ยาก': 'difficile',
    'ง่าย': 'facile'
  };
  
  // Chercher le premier mot connu dans la phrase
  for (const [word, meaning] of Object.entries(commonWords)) {
    if (phrase.includes(word)) {
      return { word, meaning };
    }
  }
  
  // Si aucun mot connu, extraire le premier groupe de 2-4 caractères thaïs
  const match = phrase.match(/[\u0E00-\u0E7F]{2,4}/);
  if (match) {
    return { word: match[0], meaning: 'mot utilisé dans cette scène' };
  }
  
  return null;
};

/**
 * PROMPT 3G: Génère un bloc fallback SPÉCIFIQUE (jamais générique)
 * Cite un mot exact de la scène et explique son sens en français
 */
const generateFallbackBlock = (blockType, storyThai, usedPhrases = []) => {
  const phrases = extractThaiPhrases(storyThai);
  const availablePhrase = phrases.find(p => !usedPhrases.includes(p)) || phrases[0];
  if (!availablePhrase) return null;
  
  // Extraire un mot clé avec son sens
  const keyWord = extractKeyWordFromPhrase(availablePhrase);
  
  const titles = {
    'phrase_utile': 'Phrase utile',
    'usage_naturel': 'Usage naturel',
    'grammaire': 'Grammaire / structure',
    'formulation': 'À retenir'
  };
  
  const icons = {
    'phrase_utile': 'MessageCircle',
    'usage_naturel': 'Users',
    'grammaire': 'BookOpen',
    'formulation': 'Lightbulb'
  };
  
  // Construire une explication SPÉCIFIQUE basée sur le mot clé
  let explanation;
  if (keyWord) {
    const templates = {
      'phrase_utile': `Dans ${availablePhrase}, le mot ${keyWord.word} signifie « ${keyWord.meaning} ». Cette phrase montre comment l'utiliser en contexte.`,
      'usage_naturel': `${keyWord.word} (« ${keyWord.meaning} ») apparaît dans ${availablePhrase}. C'est une formulation naturelle en thaï parlé.`,
      'grammaire': `${availablePhrase} contient ${keyWord.word} (« ${keyWord.meaning} »). Ce mot se place ici pour former une phrase fluide.`,
      'formulation': `Retiens ${keyWord.word} (« ${keyWord.meaning} ») tel qu'il apparaît dans ${availablePhrase}.`
    };
    explanation = templates[blockType] || templates['phrase_utile'];
  } else {
    // Fallback minimal si aucun mot clé trouvé
    explanation = `${availablePhrase} — phrase extraite de la scène.`;
  }
  
  console.log(`[FALLBACK] Bloc "${blockType}" généré depuis la scène (mot clé: ${keyWord?.word || 'aucun'})`);
  
  return {
    type: blockType,
    title: titles[blockType] || 'Phrase utile',
    icon: icons[blockType] || 'MessageCircle',
    thai_citation: availablePhrase,
    explanation_fr: explanation,
    is_fallback: true
  };
};

/**
 * PROMPT 3F: Construit les blocs structurés pour le mode Bref (exactement 2 blocs)
 * PROMPT 3G: Ajoute des logs pour tracer la source (IA vs fallback)
 */
const buildBriefBlocks = (grammarNotes, storyThai) => {
  const blocks = [];
  const usedPhrases = [];
  const stats = { ai: 0, fallback: 0 };
  
  // Bloc 1: Phrase utile
  const block1Content = validateAnchoredBlock(
    grammarNotes?.grammar || grammarNotes?.realLifeUsage,
    storyThai,
    'phrase_utile'
  );
  
  if (block1Content) {
    const thaiMatch = block1Content.match(/[\u0E00-\u0E7F]{3,}[^\s]*/);
    blocks.push({
      type: 'phrase_utile',
      title: 'Phrase utile',
      icon: 'MessageCircle',
      thai_citation: thaiMatch ? thaiMatch[0] : '',
      explanation_fr: block1Content,
      is_fallback: false
    });
    if (thaiMatch) usedPhrases.push(thaiMatch[0]);
    stats.ai++;
    console.log(`[BLOC SOURCE] "phrase_utile" → IA ✓`);
  } else {
    const fallback = generateFallbackBlock('phrase_utile', storyThai, usedPhrases);
    if (fallback) {
      blocks.push(fallback);
      usedPhrases.push(fallback.thai_citation);
      stats.fallback++;
      console.log(`[BLOC SOURCE] "phrase_utile" → FALLBACK (validation IA échouée)`);
    }
  }
  
  // Bloc 2: Usage naturel
  const block2Content = validateAnchoredBlock(
    grammarNotes?.realLifeUsage || grammarNotes?.naturalVsTextbook,
    storyThai,
    'usage_naturel'
  );
  
  if (block2Content && block2Content !== blocks[0]?.explanation_fr) {
    const thaiMatch = block2Content.match(/[\u0E00-\u0E7F]{3,}[^\s]*/);
    blocks.push({
      type: 'usage_naturel',
      title: 'Usage naturel',
      icon: 'Users',
      thai_citation: thaiMatch ? thaiMatch[0] : '',
      explanation_fr: block2Content,
      is_fallback: false
    });
    stats.ai++;
    console.log(`[BLOC SOURCE] "usage_naturel" → IA ✓`);
  } else {
    const fallback = generateFallbackBlock('usage_naturel', storyThai, usedPhrases);
    if (fallback) {
      blocks.push(fallback);
      stats.fallback++;
      console.log(`[BLOC SOURCE] "usage_naturel" → FALLBACK (validation IA échouée)`);
    }
  }
  
  // Garantir exactement 2 blocs
  while (blocks.length < 2) {
    const fallback = generateFallbackBlock(
      blocks.length === 0 ? 'phrase_utile' : 'usage_naturel',
      storyThai,
      usedPhrases
    );
    if (fallback) {
      blocks.push(fallback);
      usedPhrases.push(fallback.thai_citation);
      stats.fallback++;
      console.log(`[BLOC SOURCE] "${fallback.type}" → FALLBACK (bloc manquant)`);
    } else break;
  }
  
  // Log récapitulatif
  console.log(`[BRIEF STATS] Mode Bref: ${stats.ai} bloc(s) IA, ${stats.fallback} bloc(s) fallback`);
  
  return blocks.slice(0, 2);
};

/**
 * PROMPT 3F: Construit les blocs structurés pour le mode Détaillé (exactement 4 blocs)
 * PROMPT 3G: Ajoute des logs pour tracer la source (IA vs fallback)
 */
const buildDetailedBlocks = (grammarNotes, storyThai) => {
  const blocks = [];
  const usedPhrases = [];
  const blockTypes = ['phrase_utile', 'grammaire', 'usage_naturel', 'formulation'];
  const sources = [
    grammarNotes?.grammar,
    grammarNotes?.realLifeUsage,
    grammarNotes?.naturalVsTextbook,
    // PROMPT 3F FIX: Priorité à recap (asymétrie franco-thaïe) sur culturalContext (généralités)
    grammarNotes?.recap || grammarNotes?.culturalContext
  ];
  const titles = ['Phrase utile', 'Grammaire / structure', 'Usage naturel', 'À retenir'];
  const icons = ['MessageCircle', 'BookOpen', 'Users', 'Lightbulb'];
  const stats = { ai: 0, fallback: 0 };
  
  for (let i = 0; i < 4; i++) {
    const content = validateAnchoredBlock(sources[i], storyThai, blockTypes[i]);
    const alreadyUsed = blocks.some(b => b.explanation_fr === content);
    
    if (content && !alreadyUsed) {
      const thaiMatch = content.match(/[\u0E00-\u0E7F]{3,}[^\s]*/);
      blocks.push({
        type: blockTypes[i],
        title: titles[i],
        icon: icons[i],
        thai_citation: thaiMatch ? thaiMatch[0] : '',
        explanation_fr: content,
        is_fallback: false
      });
      if (thaiMatch) usedPhrases.push(thaiMatch[0]);
      stats.ai++;
      console.log(`[BLOC SOURCE] "${blockTypes[i]}" → IA ✓`);
    } else {
      const fallback = generateFallbackBlock(blockTypes[i], storyThai, usedPhrases);
      if (fallback) {
        blocks.push(fallback);
        usedPhrases.push(fallback.thai_citation);
        stats.fallback++;
        console.log(`[BLOC SOURCE] "${blockTypes[i]}" → FALLBACK (validation IA échouée)`);
      }
    }
  }
  
  // Log récapitulatif
  console.log(`[DETAILED STATS] Mode Détaillé: ${stats.ai} bloc(s) IA, ${stats.fallback} bloc(s) fallback`);
  
  return blocks.slice(0, 4);
};

/**
 * PROMPT 3F: Construit le bloc vocabulaire (3-5 mots)
 */
const buildVocabularyBlock = (grammarNotes, storyThai) => {
  if (grammarNotes?.vocabulary && grammarNotes.vocabulary.length > 0) {
    return grammarNotes.vocabulary.slice(0, 5).map(v => ({
      thai: v.thai,
      french: v.meaning_fr || v.french
    }));
  }
  
  // Fallback: mots courants présents dans la scène
  const vocabulary = [];
  const commonWords = {
    'ขอ': 'demander / puis-je',
    'ครับ': 'politesse (homme)',
    'ค่ะ': 'politesse (femme)',
    'คะ': 'question (femme)',
    'ได้ไหม': 'est-ce possible',
    'ขอบคุณ': 'merci',
    'สวัสดี': 'bonjour',
    'รอสักครู่': 'un instant',
    'เท่าไร': 'combien',
    'อร่อย': 'délicieux',
    'ไม่เป็นไร': 'pas de problème'
  };
  
  if (storyThai) {
    for (const [thai, french] of Object.entries(commonWords)) {
      if (storyThai.includes(thai) && vocabulary.length < 5) {
        vocabulary.push({ thai, french });
      }
    }
  }
  
  return vocabulary.slice(0, 5);
};

/**
 * PROMPT 3D BIS: Valide et nettoie les données de la scène avant utilisation
 * Rejette les réponses de refus IA et les scènes sans thaï valide
 */
const validateStoryData = (story) => {
  if (!story) return null;
  
  // PROMPT 3D BIS: Vérifier que story_thai existe
  if (!story.story_thai) {
    console.error('[PROMPT 3D BIS] story_thai absent');
    return null;
  }
  
  // PROMPT 3D BIS: Vérifier que ce n'est pas un refus IA
  if (isAIRefusal(story.story_thai)) {
    console.error('[PROMPT 3D BIS] Refus IA détecté dans story_thai');
    return null;
  }
  
  // PROMPT 3D BIS: Vérifier que la scène contient du thaï valide (pas juste du français)
  if (!containsValidThai(story.story_thai)) {
    console.error('[PROMPT 3D BIS] story_thai ne contient pas de thaï valide');
    return null;
  }
  
  // PROMPT 3D: Vérifier qu'il n'y a pas de JSON brut
  if (containsRawJSON(story.story_thai)) {
    console.error('[PROMPT 3D] story_thai contient du JSON brut');
    return null;
  }
  
  // Valider et nettoyer street_notes
  const validatedStreetNotes = validatePedagogicalBlock(story.street_notes, 'street_notes');
  
  return {
    ...story,
    street_notes: validatedStreetNotes,
    // S'assurer que ces champs existent
    story_french: story.story_french_natural || story.story_french_strict || story.story_french || '',
    words_used: Array.isArray(story.words_used) ? story.words_used : [],
    words_to_write: Array.isArray(story.words_to_write) ? story.words_to_write : []
  };
};

/**
 * PROMPT 3D: Nettoie les labels UI du texte avant envoi au TTS
 * Les labels comme "Moi:", "ผม:", "คนขับ:" ne doivent pas être lus
 */
const stripLabelsForTTS = (text) => {
  if (!text || typeof text !== 'string') return text;
  
  // Labels UI à supprimer avant TTS
  const labelPatterns = [
    /^Moi:\s*/gm,
    /^ผม:\s*/gm,
    /^ฉัน:\s*/gm,
    /^คนขับ:\s*/gm,
    /^พนักงาน:\s*/gm,
    /^เพื่อน:\s*/gm,
    /^คนขาย:\s*/gm,
    /^พ่อค้า:\s*/gm,
    /^แม่ค้า:\s*/gm,
    /^ผู้ใช้:\s*/gm,
    /^ลูกค้า:\s*/gm
  ];
  
  let cleaned = text;
  labelPatterns.forEach(pattern => {
    cleaned = cleaned.replace(pattern, '');
  });
  
  return cleaned.trim();
};

const StoryGeneratorModal = ({ isOpen, onClose }) => {
  const [wordCount, setWordCount] = useState(10);
  const [topic, setTopic] = useState("");
  const [firstPerson, setFirstPerson] = useState(false);
  const [persona, setPersona] = useState("masculine"); // PROMPT 3C: "masculine" or "feminine"
  const [isGenerating, setIsGenerating] = useState(false);
  const [story, setStory] = useState(null);
  const [voiceSpeed, setVoiceSpeed] = useState(0.8);
  
  // ============================================================
  // GLOBAL AUDIO STATE MACHINE FOR "ÉCOUTER" BUTTON
  // States: 'idle' | 'loading' | 'playing' | 'stopping' | 'error' | 'audio_ready'
  // 'audio_ready' = chunks preloaded but Safari blocked autoplay - waiting for user tap
  // ============================================================
  const [globalAudioState, setGlobalAudioState] = useState('idle');
  
  // Legacy states kept for compatibility with other parts
  const [isPlayingStory, setIsPlayingStory] = useState(false);
  const [isLoadingStoryAudio, setIsLoadingStoryAudio] = useState(false);
  const [showLearningSupport, setShowLearningSupport] = useState(false);
  const [practiceWord, setPracticeWord] = useState(null);
  const [practiceCount, setPracticeCount] = useState(0);
  const [practiceInput, setPracticeInput] = useState("");
  const [completedWords, setCompletedWords] = useState([]);
  const [isLoadingGrammar, setIsLoadingGrammar] = useState(false);
  const [grammarNotes, setGrammarNotes] = useState(null);
  const [grammarNotesStructured, setGrammarNotesStructured] = useState(null); // NEW: Structured JSON data
  // Mode selector: "story" or "phrases"
  const [mode, setMode] = useState("story");
  // Phrase generator states - NEW FLUENCY TRAINING
  const [phraseMode, setPhraseMode] = useState("standard"); // "standard" or "progressive"
  const [phraseCount, setPhraseCount] = useState(2);
  const [phraseLevel, setPhraseLevel] = useState("simple");
  const [phraseTopic, setPhraseTopic] = useState(""); // Custom topic for phrases
  const [phrases, setPhrases] = useState([]);
  const [phrasesData, setPhrasesData] = useState(null); // Full response with tips
  const [isGeneratingPhrases, setIsGeneratingPhrases] = useState(false);
  const [selectedPhrase, setSelectedPhrase] = useState(null); // For detailed view
  const [showExercise, setShowExercise] = useState(false);
  const [exerciseAnswer, setExerciseAnswer] = useState("");
  const [exerciseResult, setExerciseResult] = useState(null);
  // Progressive phrase building
  const [progressivePhrases, setProgressivePhrases] = useState([]);
  const [currentStep, setCurrentStep] = useState(0); // Current visible step in progressive mode
  const [selectedProgressivePhrase, setSelectedProgressivePhrase] = useState(null);
  const [progressiveKeyword, setProgressiveKeyword] = useState(""); // Custom keyword for progressive mode
  // History states
  const [phraseHistory, setPhraseHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  // Pronunciation test states
  const [isRecording, setIsRecording] = useState(false);
  const [pronunciationResult, setPronunciationResult] = useState(null);
  const [isTestingPronunciation, setIsTestingPronunciation] = useState(false);
  // Sentence-by-sentence test mode
  const [sentenceTestMode, setSentenceTestMode] = useState(false);
  const [selectedSentence, setSelectedSentence] = useState(null);
  const [sentenceResult, setSentenceResult] = useState(null);
  const [testingSentenceIndex, setTestingSentenceIndex] = useState(null);
  // Manee chat state
  const [showManeeChat, setShowManeeChat] = useState(false);
  // Quiz states
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [quizAnswers, setQuizAnswers] = useState([]);
  const [quizResult, setQuizResult] = useState(null);
  const [isLoadingQuiz, setIsLoadingQuiz] = useState(false);
  const [currentQuizQuestion, setCurrentQuizQuestion] = useState(0);
  // PHASE 1: Translation hidden by default - user must click to reveal
  const [showTranslation, setShowTranslation] = useState(false);
  // PROMPT 3C: Section "Pratiquer l'écriture" repliée par défaut
  const [showWritingPractice, setShowWritingPractice] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const storyAudioRef = useRef(null);
  const practiceInputRef = useRef(null);

  // ============================================================
  // PHRASES AUDIO STATE MACHINE - For Phrases quotidiennes module
  // Implements proper pause/resume without creating new Audio instances
  // ============================================================
  const phraseAudioRef = useRef(null);          // Current Audio instance
  const phraseAudioUrlRef = useRef(null);       // Current blob URL
  const phraseAudioTextRef = useRef(null);      // Text being played (to detect phrase change)
  const [phraseAudioState, setPhraseAudioState] = useState('idle'); // 'idle' | 'loading' | 'playing' | 'paused'

  // Difficulty levels for fluency training
  const phraseLevels = [
    { 
      id: "simple", 
      label: "🌱 Simple", 
      desc: "2-4 mots", 
      detail: "Bases essentielles pour commencer",
      color: "from-green-400 to-emerald-500"
    },
    { 
      id: "intermediate", 
      label: "🌿 Intermédiaire", 
      desc: "5-8 mots", 
      detail: "Conversations courantes du quotidien",
      color: "from-amber-400 to-orange-500"
    },
    { 
      id: "advanced", 
      label: "🌳 Avancé", 
      desc: "8-15 mots", 
      detail: "Expression fluide et nuancée",
      color: "from-purple-400 to-violet-500"
    },
  ];

  // Suggested topics for phrase generation
  const phraseTopicSuggestions = [
    "🍜 Commander quelque chose",
    "💰 Demander le prix",
    "❓ Dire qu'on ne comprend pas",
    "🔄 Demander de répéter",
    "🌶️ Demander moins épicé",
    "⏰ Prévenir d'un retard",
    "😴 Dire qu'on est fatigué",
    "🚏 Demander où descendre",
    "📖 Dire qu'on apprend le thaï",
    "🙏 Refuser poliment"
  ];

  // SCÈNES DU QUOTIDIEN - Missions concrètes, pas thèmes vagues
  const suggestedTopics = [
    "Commander un café moins sucré",
    "Demander le prix au marché",
    "Dire qu'on ne comprend pas",
    "Demander moins épicé",
    "Prévenir d'un retard",
    "Demander l'addition",
    "Dire qu'on apprend le thaï",
    "Clarifier un malentendu",
    "Signaler un problème à l'hôtel",
    "Demander au taxi de suivre la carte"
  ];
  
  const firstPersonTopics = [
    "🍜 Commander un plat moins épicé",
    "💰 Demander combien ça coûte",
    "❓ Dire que je ne comprends pas",
    "🔄 Demander de répéter lentement",
    "⏰ Prévenir que je serai en retard",
    "📍 Demander où je dois descendre",
    "💳 Demander l'addition",
    "🏨 Signaler que la clim ne marche pas",
    "📖 Expliquer que j'apprends le thaï",
    "🙏 Refuser poliment une proposition"
  ];

  // Generate daily phrases for fluency training
  const generatePhrases = async () => {
    // Clean up any playing audio when generating new phrases
    cleanupPhraseAudio();
    
    setIsGeneratingPhrases(true);
    setPhrases([]);
    setProgressivePhrases([]);
    setPhrasesData(null);
    setSelectedPhrase(null);
    setSelectedProgressivePhrase(null);
    setShowExercise(false);
    
    try {
      if (phraseMode === "progressive") {
        // Progressive mode - phrases that expand step by step
        const response = await axios.post(`${API}/phrases/generate-progressive`, {
          count: phraseCount,
          level: phraseLevel,
          topic: phraseTopic || null,
          keyword: progressiveKeyword || null  // Custom keyword for progressive phrases
        });
        
        // Validate response structure
        const phrases = response.data?.phrases || [];
        if (!Array.isArray(phrases)) {
          throw new Error("Invalid response structure");
        }
        
        setProgressivePhrases(phrases);
        setPhrasesData(response.data);
        toast.success(`${response.data.total || phrases.length} phrases progressives générées !`);
        
        // Log activity in try-catch to prevent any side effects
        try {
          logActivity('progressive_phrases_generated', null, { count: phraseCount, level: phraseLevel, topic: phraseTopic, keyword: progressiveKeyword });
        } catch (logErr) {
          console.error("Activity logging failed:", logErr);
        }
      } else {
        // Standard mode
        const response = await axios.post(`${API}/phrases/generate`, {
          count: phraseCount,
          level: phraseLevel,
          topic: phraseTopic || null  // Include custom topic
        });
        setPhrases(response.data.phrases || []);
        setPhrasesData(response.data);
        toast.success(`${response.data.total} phrases générées - Niveau ${phraseLevel} !`);
        logActivity('phrases_generated', null, { count: phraseCount, level: phraseLevel, topic: phraseTopic });
      }
    } catch (err) {
      console.error("Phrase generation error:", err);
      if (err.response?.status === 503) {
        toast.info("Mode données vérifiées activé. ThaiPractice affiche uniquement les données pédagogiques vérifiées.");
      } else {
        toast.error("Erreur lors de la génération des phrases. Réessayez.");
      }
    } finally {
      setIsGeneratingPhrases(false);
    }
  };

  // Check exercise answer
  const checkExerciseAnswer = (phrase) => {
    if (!phrase?.exercise?.answer) return;
    const userAnswer = exerciseAnswer.trim();
    const correctAnswer = phrase.exercise.answer.trim();
    
    if (userAnswer === correctAnswer) {
      setExerciseResult({ correct: true, message: "Parfait ! 🎉" });
      toast.success("Bonne réponse !");
    } else {
      setExerciseResult({ 
        correct: false, 
        message: `Réponse attendue: ${correctAnswer}`
      });
    }
  };

  // Fetch phrase history
  const fetchPhraseHistory = async () => {
    try {
      const response = await axios.get(`${API}/generation-history/phrases?limit=15`);
      setPhraseHistory(response.data.history || []);
    } catch (err) {
      console.error("Error fetching history:", err);
    }
  };

  // Clear history
  const clearHistory = async (type = "all") => {
    try {
      await axios.delete(`${API}/generation-history/clear?type=${type}`);
      setPhraseHistory([]);
      toast.success("Historique effacé !");
    } catch (err) {
      console.error("Error clearing history:", err);
      toast.error("Erreur lors de l'effacement");
    }
  };

  const generateStory = async () => {
    // CRITICAL: Stop and cleanup ANY existing global audio before generating new story
    // This includes 'audio_ready' state where chunks are preloaded but not yet played
    if (globalAudioState === 'playing' || globalAudioState === 'loading' || globalAudioState === 'audio_ready') {
      logGlobalAudio(`🧹 [SYNC] Cleanup triggered by NEW STORY generation (was: ${globalAudioState})`);
      cleanupGlobalAudio('new_story');
    }
    
    setIsGenerating(true);
    setStory(null);
    setShowLearningSupport(false);
    setGrammarNotes(null);
    setCompletedWords([]);
    setPracticeWord(null);
    setPronunciationResult(null);
    setShowQuiz(false);
    setQuizQuestions([]);
    setQuizAnswers([]);
    setQuizResult(null);
    // PHASE 1: Complete state reset for new story
    setGrammarNotesStructured(null);
    setPracticeCount(0);
    setPracticeInput("");
    setSentenceTestMode(false);
    setSentenceResult(null);
    setTestingSentenceIndex(null);
    setShowTranslation(false);
    setCurrentQuizQuestion(0);
    // PROMPT 3C: Reset writing practice section to collapsed
    setShowWritingPractice(false);
    try {
      const response = await axios.post(`${API}/story/generate`, { 
        word_count: wordCount,
        topic: topic || null,
        first_person: firstPerson,
        persona: persona // PROMPT 3C: "masculine" or "feminine"
      });
      
      // PROMPT 3D: Valider et nettoyer les données avant affichage
      const validatedStory = validateStoryData(response.data);
      if (!validatedStory) {
        toast.error("Le support n'a pas pu être généré correctement. Réessaie.");
        return;
      }
      
      setStory(validatedStory);
      const modeLabel = firstPerson ? "Ma Scène" : "Scène";
      toast.success(`${modeLabel} générée avec ${wordCount} mots !`);
      logActivity('story_read', null, { wordCount, topic, firstPerson, persona });
    } catch (err) {
      console.error("Story generation error:", err);
      if (err.response?.status === 503) {
        toast.info("Mode données vérifiées activé. ThaiPractice affiche uniquement les données pédagogiques vérifiées.");
      } else {
        toast.error("Erreur lors de la génération. Réessayez.");
      }
    } finally {
      setIsGenerating(false);
    }
  };

  // Quiz functions
  const startQuiz = async () => {
    if (!story) return;
    
    setIsLoadingQuiz(true);
    setShowQuiz(true);
    setQuizResult(null);
    setQuizAnswers([]);
    setCurrentQuizQuestion(0);
    
    try {
      const response = await axios.post(`${API}/story/quiz/generate`, {
        story_thai: story.story_thai,
        story_french: story.story_french_natural || story.story_french_strict,
        words_used: story.words_used
      });
      setQuizQuestions(response.data.questions);
      setQuizAnswers(new Array(response.data.questions.length).fill(-1));
      toast.success("Quiz prêt ! Bonne chance 🍀");
    } catch (err) {
      console.error("Quiz generation error:", err);
      toast.error("Erreur lors de la génération du quiz");
      setShowQuiz(false);
    } finally {
      setIsLoadingQuiz(false);
    }
  };

  const selectAnswer = (questionIndex, answerIndex) => {
    const newAnswers = [...quizAnswers];
    newAnswers[questionIndex] = answerIndex;
    setQuizAnswers(newAnswers);
  };

  const submitQuiz = async () => {
    if (quizAnswers.includes(-1)) {
      toast.error("Réponds à toutes les questions avant de valider !");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/story/quiz/evaluate`, {
        questions: quizQuestions,
        user_answers: quizAnswers
      });
      setQuizResult(response.data);
    } catch (err) {
      console.error("Quiz evaluation error:", err);
      toast.error("Erreur lors de l'évaluation du quiz");
    }
  };

  // Pronunciation test functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await testPronunciation(audioBlob);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      toast.info("🎤 Enregistrement... Lis l'histoire à voix haute !");
    } catch (err) {
      console.error("Microphone error:", err);
      toast.error("Impossible d'accéder au microphone");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // ============================================================
  // STRICT AUDIO TEXT NORMALIZATION FUNCTION
  // Separate from display text - only for TTS
  // ============================================================
  const normalizeTextForAudio = (text) => {
    if (!text) return '';
    
    let normalized = text;
    
    // 1. Remove special markers <<standard||street>> - keep only standard
    normalized = normalized.replace(/<<([^|]+)\|\|[^>]+>>/g, '$1');
    
    // 2. Remove technical markers like "pause rythmique", "= pause", etc.
    normalized = normalized.replace(/[·•]\s*pause\s*rythmique/gi, '');
    normalized = normalized.replace(/pause\s*rythmique/gi, '');
    normalized = normalized.replace(/[·•]/g, ''); // Remove rhythm markers
    
    // 3. Remove all multiple spaces (replace with single space)
    normalized = normalized.replace(/[ \t]+/g, ' ');
    
    // 4. Remove multiple newlines (replace with single space for audio continuity)
    normalized = normalized.replace(/[\r\n]+/g, ' ');
    
    // 5. Remove empty quoted strings
    normalized = normalized.replace(/[""][""]/g, '');
    normalized = normalized.replace(/''/g, '');
    
    // 6. Remove leading/trailing whitespace on the whole text
    normalized = normalized.trim();
    
    // 7. Final cleanup - collapse any remaining multiple spaces
    normalized = normalized.replace(/\s{2,}/g, ' ');
    
    return normalized;
  };

  // Split normalized text into clean audio segments
  // CRITICAL FIX: Ensure ALL segments including the LAST one are captured
  const splitTextIntoAudioSegments = (normalizedText) => {
    if (!normalizedText) return [];
    
    const trimmedText = normalizedText.trim();
    if (!trimmedText) return [];
    
    // Thai sentence-ending particles
    const endingParticles = 'ครับค่ะคะนะสิเลยไหมมาได้แล้ว';
    
    // CRITICAL FIX: Use a different approach that doesn't lose the last segment
    // Split at positions AFTER sentence-ending particles followed by space
    // but ensure we capture everything including the final segment
    
    const segments = [];
    let currentSegment = '';
    const words = trimmedText.split(/\s+/);
    
    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      currentSegment += (currentSegment ? ' ' : '') + word;
      
      // Check if word ends with a sentence-ending particle
      const lastChar = word.slice(-1);
      const endsWithParticle = endingParticles.includes(lastChar);
      
      // Check if next word starts with Thai character (indicates new sentence)
      const nextWord = words[i + 1];
      const nextStartsThai = nextWord && /^[ก-ฮ]/.test(nextWord);
      
      // Create segment break if:
      // 1. Word ends with particle AND next word starts with Thai char
      // 2. OR we're at the last word
      if ((endsWithParticle && nextStartsThai) || i === words.length - 1) {
        if (currentSegment.trim()) {
          segments.push(currentSegment.trim());
        }
        currentSegment = '';
      }
    }
    
    // CRITICAL: If there's any remaining text, add it as the final segment
    if (currentSegment.trim()) {
      segments.push(currentSegment.trim());
    }
    
    // Final validation: remove any empty segments
    const validSegments = segments.filter(seg => seg && seg.trim().length > 0);
    
    console.log(`[splitTextIntoAudioSegments] Input: "${trimmedText.substring(0, 50)}..." (${trimmedText.length} chars)`);
    console.log(`[splitTextIntoAudioSegments] Output: ${validSegments.length} segments`);
    validSegments.forEach((seg, i) => {
      console.log(`  Segment ${i + 1}: "${seg.substring(0, 40)}${seg.length > 40 ? '...' : ''}" (${seg.length} chars)`);
    });
    
    return validSegments;
  };

  // Improved sentence parsing for phrase-by-phrase mode
  // STRICT: More granular splitting for pedagogical use
  const parseStoryIntoSentences = (thaiText) => {
    if (!thaiText) return [];
    
    // Clean special markers first
    const cleanText = thaiText.replace(/<<([^|]+)\|\|[^>]+>>/g, '$1');
    
    const sentences = [];
    
    // First split by newlines to get paragraphs/dialogue lines
    const lines = cleanText.split('\n').filter(line => line.trim());
    
    lines.forEach((line, lineIndex) => {
      const trimmedLine = line.trim();
      
      // Check if it's a dialogue line (Speaker: content)
      const dialogueMatch = trimmedLine.match(/^([^:]+):\s*[""]?(.+?)[""]?$/);
      
      if (dialogueMatch) {
        const speaker = dialogueMatch[1].trim();
        const content = dialogueMatch[2].trim();
        
        // Split dialogue content by Thai sentence-ending particles
        // More aggressive split: look for natural breaks at ครับ, ค่ะ, คะ, นะ, สิ, เลย, ไหม, มา, ได้, แล้ว
        // followed by space OR end of string
        const parts = content
          .split(/(?<=[ครับค่ะคะนะสิเลยไหมมาได้แล้ว])\s+/)
          .filter(p => p.trim() && p.trim().length > 2);
        
        if (parts.length > 1) {
          parts.forEach((part, i) => {
            sentences.push({
              id: `${lineIndex}-${i}`,
              speaker: i === 0 ? speaker : null,
              text: part.trim(),
              fullLine: i === 0 ? `${speaker}: ${part.trim()}` : part.trim()
            });
          });
        } else {
          // If dialogue is short or can't be split, keep as single phrase
          sentences.push({
            id: `${lineIndex}-0`,
            speaker: speaker,
            text: content,
            fullLine: trimmedLine
          });
        }
      } else {
        // Narrative line - split by natural Thai sentence boundaries
        // Split on: sentence-ending particles followed by space and Thai char
        // Also consider comma-like pauses for longer segments
        const parts = trimmedLine
          .split(/(?<=[ครับค่ะคะนะสิเลยไหมมาได้แล้ว])\s+(?=[ก-ฮA-Za-z])/)
          .filter(p => p.trim() && p.trim().length > 2);
        
        if (parts.length > 1) {
          parts.forEach((part, i) => {
            sentences.push({
              id: `${lineIndex}-${i}`,
              speaker: null,
              text: part.trim(),
              fullLine: part.trim()
            });
          });
        } else {
          // If line is long (>50 chars) but couldn't be split by particles,
          // try splitting by spaces after certain natural break points
          if (trimmedLine.length > 50) {
            // Try to find natural breaks mid-sentence
            const midParts = trimmedLine
              .split(/\s+(?=[ก-ฮ])/)
              .reduce((acc, word, idx, arr) => {
                if (acc.length === 0) {
                  acc.push(word);
                } else {
                  const lastPart = acc[acc.length - 1];
                  // Keep building until we hit ~30-40 chars
                  if (lastPart.length < 35) {
                    acc[acc.length - 1] = lastPart + ' ' + word;
                  } else {
                    acc.push(word);
                  }
                }
                return acc;
              }, [])
              .filter(p => p.trim());
            
            if (midParts.length > 1) {
              midParts.forEach((part, i) => {
                sentences.push({
                  id: `${lineIndex}-${i}`,
                  speaker: null,
                  text: part.trim(),
                  fullLine: part.trim()
                });
              });
            } else {
              sentences.push({
                id: `${lineIndex}-0`,
                speaker: null,
                text: trimmedLine,
                fullLine: trimmedLine
              });
            }
          } else {
            sentences.push({
              id: `${lineIndex}-0`,
              speaker: null,
              text: trimmedLine,
              fullLine: trimmedLine
            });
          }
        }
      }
    });
    
    // STRICT: Filter out any empty or whitespace-only sentences
    // Each sentence must have real content
    return sentences.filter(s => s.text && s.text.trim().length > 0);
  };

  // Normalize sentence text for audio in phrase-by-phrase mode
  const normalizeSentenceForAudio = (text) => {
    if (!text) return '';
    let normalized = text;
    // Remove rhythm markers
    normalized = normalized.replace(/[·•]/g, '');
    // Remove multiple spaces
    normalized = normalized.replace(/\s+/g, ' ');
    // Trim
    return normalized.trim();
  };

  const startSentenceRecording = async (sentence, index) => {
    setSelectedSentence(sentence);
    setTestingSentenceIndex(index);
    setSentenceResult(null);
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await testSentencePronunciation(audioBlob, sentence);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      toast.info(`🎤 Lis: "${sentence.text.slice(0, 30)}..."`);
    } catch (err) {
      console.error("Microphone error:", err);
      toast.error("Impossible d'accéder au microphone");
      setTestingSentenceIndex(null);
    }
  };

  const stopSentenceRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const testSentencePronunciation = async (audioBlob, sentence) => {
    setIsTestingPronunciation(true);
    
    try {
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      
      reader.onloadend = async () => {
        const base64Audio = reader.result.split(',')[1];
        
        try {
          const response = await axios.post(`${API}/pronunciation/test`, {
            expected_text: sentence.text,
            audio_base64: base64Audio
          });
          
          setSentenceResult({
            sentenceId: sentence.id,
            ...response.data
          });
          
          logActivity('practice_character', sentence.text.slice(0, 20));
          
          if (response.data.score >= 85) {
            toast.success(`🎯 ${response.data.score}% - Excellent !`);
          } else if (response.data.score >= 70) {
            toast.info(`👍 ${response.data.score}% - Bien !`);
          } else {
            toast.warning(`⚠️ ${response.data.score}% - À améliorer`);
          }
        } catch (err) {
          console.error("Pronunciation test error:", err);
          toast.error("Erreur lors du test");
        } finally {
          setIsTestingPronunciation(false);
          setTestingSentenceIndex(null);
        }
      };
    } catch (err) {
      console.error("Audio processing error:", err);
      setIsTestingPronunciation(false);
      setTestingSentenceIndex(null);
    }
  };

  const playSentence = async (text) => {
    // STRICT: Normalize text before sending to TTS
    const normalizedText = normalizeSentenceForAudio(text);
    
    if (!normalizedText) {
      console.warn('[playSentence] Empty text after normalization, skipping');
      return;
    }
    
    try {
      // Use context=story for calmer, more natural narration tone
      const response = await axios.post(
        `${API}/tts?text=${encodeURIComponent(normalizedText)}&speed=${voiceSpeed}&context=story`,
        null,
        { responseType: 'blob' }
      );
      const audioUrl = URL.createObjectURL(response.data);
      const audio = new Audio(audioUrl);
      audio.onended = () => URL.revokeObjectURL(audioUrl);
      await audio.play();
    } catch (err) {
      console.error("TTS error:", err);
      toast.error("Erreur audio");
    }
  };

  const testPronunciation = async (audioBlob) => {
    if (!story) return;
    
    setIsTestingPronunciation(true);
    setPronunciationResult(null);
    
    try {
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      
      reader.onloadend = async () => {
        const base64Audio = reader.result.split(',')[1];
        const cleanStory = story.story_thai.replace(/<<([^|]+)\|\|[^>]+>>/g, '$1');
        
        try {
          const response = await axios.post(`${API}/pronunciation/test`, {
            expected_text: cleanStory,
            audio_base64: base64Audio
          });
          
          setPronunciationResult(response.data);
          
          if (response.data.score >= 70) {
            toast.success(`Score: ${response.data.score}% - ${response.data.feedback}`);
          } else {
            toast.info(`Score: ${response.data.score}% - ${response.data.feedback}`);
          }
        } catch (err) {
          console.error("Pronunciation test error:", err);
          toast.error("Erreur lors du test de prononciation");
        } finally {
          setIsTestingPronunciation(false);
        }
      };
    } catch (err) {
      console.error("Audio processing error:", err);
      toast.error("Erreur de traitement audio");
      setIsTestingPronunciation(false);
    }
  };

  const loadGrammarNotes = async (forceReload = false, modeOverride = null) => {
    if (!story || (grammarNotes && !forceReload)) return;
    setIsLoadingGrammar(true);
    setGrammarNotesStructured(null); // Reset structured data
    try {
      // Toujours utiliser le mode 'detailed' (mode Bref supprimé)
      const response = await axios.post(`${API}/story/grammar-notes`, {
        story_thai: story.story_thai,
        story_french: story.story_french_natural || story.story_french_strict,
        words_used: story.words_used,
        mode: 'detailed'
      });
      setGrammarNotes(response.data.grammar_notes);
      // Store structured data if available (new format)
      if (response.data.structured) {
        setGrammarNotesStructured(response.data.structured);
      }
    } catch (err) {
      console.error("Grammar notes error:", err);
      toast.error("Erreur lors du chargement des notes grammaticales");
    } finally {
      setIsLoadingGrammar(false);
    }
  };

  const openLearningSupport = () => {
    setShowLearningSupport(true);
    loadGrammarNotes();
  };

  const startPracticeWord = (word) => {
    setPracticeWord(word);
    setPracticeCount(0);
    setPracticeInput("");
    setTimeout(() => {
      if (practiceInputRef.current) {
        practiceInputRef.current.focus();
      }
    }, 100);
  };

  const handlePracticeSubmit = (e) => {
    e.preventDefault();
    if (!practiceWord) return;
    
    const cleanInput = practiceInput.trim();
    const cleanTarget = practiceWord.thai.trim();
    
    if (cleanInput === cleanTarget) {
      const newCount = practiceCount + 1;
      setPracticeCount(newCount);
      setPracticeInput("");
      
      speakWithSpeed(practiceWord.thai);
      
      if (newCount >= 5) {
        setCompletedWords([...completedWords, practiceWord.thai]);
        toast.success(`${practiceWord.thai} maîtrisé ! (5/5)`);
        setPracticeWord(null);
        setPracticeCount(0);
      }
    } else {
      toast.error("Pas tout à fait... Réessaie !");
    }
  };

  const speakWithSpeed = async (text) => {
    try {
      // Stop any currently playing audio first
      if (storyAudioRef.current) {
        storyAudioRef.current.pause();
        storyAudioRef.current = null;
      }
      
      // Use context=story for calmer, more natural narration tone
      const res = await axios.post(
        `${API}/tts?text=${encodeURIComponent(text)}&speed=${voiceSpeed}&context=story`,
        null,
        { responseType: 'blob', timeout: 30000 }
      );
      
      // Verify we got audio data
      if (!res.data || res.data.size === 0) {
        throw new Error("Réponse audio vide");
      }
      
      const audioUrl = URL.createObjectURL(res.data);
      const audio = new Audio(audioUrl);
      
      // Store reference to allow stopping
      storyAudioRef.current = audio;
      
      // Clean up blob URL when done
      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
        storyAudioRef.current = null;
      };
      
      audio.onerror = (e) => {
        console.error("Audio playback error:", e);
        URL.revokeObjectURL(audioUrl);
        storyAudioRef.current = null;
        toast.error("Erreur de lecture audio");
      };
      
      await audio.play();
      return audio;
    } catch (err) {
      console.error("TTS error:", err);
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        toast.error("Timeout - réessayez");
      } else {
        toast.error("Erreur lors de la lecture audio");
      }
      return null;
    }
  };

  // ============================================================
  // PHRASE AUDIO - Proper Play/Pause/Resume for Phrases quotidiennes
  // This function handles the audio button in the Phrases module
  // with true pause/resume capability (no new Audio on resume)
  // ============================================================
  const togglePhraseAudio = async (text) => {
    if (!text) return;
    
    // CASE 1: Currently loading - ignore all clicks
    if (phraseAudioState === 'loading') {
      console.log("[PhraseAudio] Loading in progress, ignoring click");
      return;
    }
    
    // CASE 2: Same phrase is playing - PAUSE it
    if (phraseAudioState === 'playing' && phraseAudioTextRef.current === text && phraseAudioRef.current) {
      console.log("[PhraseAudio] Pausing current audio");
      phraseAudioRef.current.pause();
      setPhraseAudioState('paused');
      return;
    }
    
    // CASE 3: Same phrase is paused - RESUME from current position
    if (phraseAudioState === 'paused' && phraseAudioTextRef.current === text && phraseAudioRef.current) {
      console.log("[PhraseAudio] Resuming from paused position");
      try {
        await phraseAudioRef.current.play();
        setPhraseAudioState('playing');
      } catch (err) {
        console.error("[PhraseAudio] Resume error:", err);
        // Reset state on error
        cleanupPhraseAudio();
      }
      return;
    }
    
    // CASE 4: Different phrase OR idle state - Stop old, start new
    console.log("[PhraseAudio] Starting new audio for:", text.substring(0, 30));
    
    // Clean up any existing audio first
    cleanupPhraseAudio();
    
    // Set loading state
    setPhraseAudioState('loading');
    phraseAudioTextRef.current = text;
    
    try {
      // Request TTS from backend
      const res = await axios.post(
        `${API}/tts?text=${encodeURIComponent(text)}&speed=${voiceSpeed}&context=phrase`,
        null,
        { responseType: 'blob', timeout: 30000 }
      );
      
      // Check if we're still expecting this response (user might have clicked elsewhere)
      if (phraseAudioTextRef.current !== text) {
        console.log("[PhraseAudio] Stale response, discarding");
        return;
      }
      
      // Verify we got audio data
      if (!res.data || res.data.size === 0) {
        throw new Error("Empty audio response");
      }
      
      // Create audio element
      const audioUrl = URL.createObjectURL(res.data);
      const audio = new Audio(audioUrl);
      
      // Store references
      phraseAudioRef.current = audio;
      phraseAudioUrlRef.current = audioUrl;
      
      // Event handlers
      audio.onplay = () => {
        console.log("[PhraseAudio] Playing");
        setPhraseAudioState('playing');
      };
      
      audio.onpause = () => {
        // Only set paused if not ended (onended will handle that)
        if (phraseAudioRef.current && !audio.ended) {
          console.log("[PhraseAudio] Paused at", audio.currentTime);
          setPhraseAudioState('paused');
        }
      };
      
      audio.onended = () => {
        console.log("[PhraseAudio] Playback ended");
        cleanupPhraseAudio();
      };
      
      audio.onerror = (e) => {
        console.error("[PhraseAudio] Error:", e);
        cleanupPhraseAudio();
        toast.error("Erreur de lecture audio");
      };
      
      // Start playback
      await audio.play();
      
    } catch (err) {
      console.error("[PhraseAudio] TTS error:", err);
      cleanupPhraseAudio();
      
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        toast.error("Timeout - réessayez");
      } else {
        toast.error("Erreur lors de la lecture audio");
      }
    }
  };
  
  // Helper function to clean up phrase audio completely
  const cleanupPhraseAudio = () => {
    console.log("[PhraseAudio] Cleanup");
    
    if (phraseAudioRef.current) {
      phraseAudioRef.current.pause();
      phraseAudioRef.current.onplay = null;
      phraseAudioRef.current.onpause = null;
      phraseAudioRef.current.onended = null;
      phraseAudioRef.current.onerror = null;
      phraseAudioRef.current = null;
    }
    
    if (phraseAudioUrlRef.current) {
      URL.revokeObjectURL(phraseAudioUrlRef.current);
      phraseAudioUrlRef.current = null;
    }
    
    phraseAudioTextRef.current = null;
    setPhraseAudioState('idle');
  };

  // ============================================================
  // GLOBAL AUDIO STATE MACHINE - STRICT IMPLEMENTATION
  // For the "Écouter" button in "Histoire en thaï" block ONLY
  // ============================================================
  
  // Single audio element ref for global story playback
  const globalAudioRef = useRef(null);
  
  // Session tracking to prevent ghost playback
  const globalAudioSessionRef = useRef({
    sessionId: null,
    segments: [],
    currentIndex: 0,
    isActive: false,
    storyAudioKey: null // CRITICAL: Unique key to match audio with displayed story
  });
  
  // Generate unique session ID
  const generateSessionId = () => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  // CRITICAL: Generate unique key for story audio synchronization
  // This ensures audio ALWAYS matches the currently displayed story
  const generateStoryAudioKey = (thaiText, wordCountParam, speedParam) => {
    if (!thaiText) return null;
    // Simple hash of thai text to detect content changes
    let hash = 0;
    for (let i = 0; i < thaiText.length; i++) {
      const char = thaiText.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    const key = `story_${wordCountParam}_${speedParam}_${Math.abs(hash)}`;
    return key;
  };
  
  // STRICT: Log function for global audio only
  const logGlobalAudio = (message, data = null) => {
    const sessionId = globalAudioSessionRef.current.sessionId || 'no-session';
    const prefix = `[GlobalAudio][${sessionId.substring(0, 20)}]`;
    if (data) {
      console.log(`${prefix} ${message}`, data);
    } else {
      console.log(`${prefix} ${message}`);
    }
  };
  
  // STRICT: Cleanup function with reason logging
  // ONLY allowed reasons: 'user_stop', 'new_story', 'component_unmount', 'audio_error', 'playback_complete'
  const cleanupGlobalAudio = (reason) => {
    const allowedReasons = ['user_stop', 'new_story', 'component_unmount', 'audio_error', 'playback_complete'];
    
    // Get call stack for debugging
    const stack = new Error().stack;
    const callerLine = stack ? stack.split('\n')[2]?.trim() : 'unknown';
    
    logGlobalAudio(`🧹 [CLEANUP] CALLED with reason: "${reason}"`);
    logGlobalAudio(`   Caller: ${callerLine}`);
    logGlobalAudio(`   Current globalAudioState: ${globalAudioState}`);
    logGlobalAudio(`   Current sessionId: ${globalAudioSessionRef.current.sessionId}`);
    logGlobalAudio(`   Current requestId: ${globalAudioSessionRef.current.requestId}`);
    logGlobalAudio(`   Preloaded URLs count: ${globalAudioSessionRef.current.preloadedUrls?.length || 0}`);
    
    if (!allowedReasons.includes(reason)) {
      logGlobalAudio(`⚠️ [CLEANUP] INVALID REASON: "${reason}" - BLOCKED!`);
      return;
    }
    
    // Invalidate session
    const oldSessionId = globalAudioSessionRef.current.sessionId;
    const oldRequestId = globalAudioSessionRef.current.requestId;
    
    // Cleanup preloaded URLs if any (for preloaded-chunks mode)
    if (globalAudioSessionRef.current.preloadedUrls && globalAudioSessionRef.current.preloadedUrls.length > 0) {
      logGlobalAudio(`🗑️ [CLEANUP] Releasing ${globalAudioSessionRef.current.preloadedUrls.length} preloaded audio URLs`);
      globalAudioSessionRef.current.preloadedUrls.forEach((url, i) => {
        try { 
          URL.revokeObjectURL(url); 
          logGlobalAudio(`   Released URL ${i + 1}`);
        } catch (e) {
          logGlobalAudio(`   Failed to release URL ${i + 1}: ${e.message}`);
        }
      });
    }
    
    globalAudioSessionRef.current.sessionId = null;
    globalAudioSessionRef.current.requestId = null;
    globalAudioSessionRef.current.isActive = false;
    globalAudioSessionRef.current.preloadedBlobs = [];
    globalAudioSessionRef.current.preloadedUrls = [];
    globalAudioSessionRef.current.resumeChunkIndex = null;
    globalAudioSessionRef.current.chunksForResume = null;
    globalAudioSessionRef.current.storyAudioKey = null; // CRITICAL: Clear audio key
    
    // Stop and cleanup audio element
    if (globalAudioRef.current) {
      logGlobalAudio(`🔇 [CLEANUP] Stopping audio element`);
      try {
        globalAudioRef.current.pause();
        logGlobalAudio(`   Audio paused`);
        globalAudioRef.current.onended = null;
        globalAudioRef.current.onerror = null;
        globalAudioRef.current.onpause = null;
        globalAudioRef.current.onabort = null;
        globalAudioRef.current.onstalled = null;
        globalAudioRef.current.onemptied = null;
        globalAudioRef.current.src = '';
        globalAudioRef.current.load();
        logGlobalAudio(`   Audio element reset`);
      } catch (e) {
        logGlobalAudio(`   Cleanup error (ignored): ${e.message}`);
      }
      globalAudioRef.current = null;
    } else {
      logGlobalAudio(`   No audio element to clean`);
    }
    
    // Reset state machine to idle
    logGlobalAudio(`🔄 [CLEANUP] Resetting state machine to 'idle'`);
    setGlobalAudioState('idle');
    setIsPlayingStory(false);
    setIsLoadingStoryAudio(false);
    
    logGlobalAudio(`✅ [CLEANUP] COMPLETE`);
    logGlobalAudio(`   Old sessionId: ${oldSessionId}`);
    logGlobalAudio(`   Old requestId: ${oldRequestId}`);
    logGlobalAudio(`   New state: idle`);
  };
  
  // STRICT: Normalize and split text into valid audio segments
  const prepareAudioSegments = (thaiText) => {
    if (!thaiText) return [];
    
    // Step 1: Remove special markers
    let text = thaiText.replace(/<<([^|]+)\|\|[^>]+>>/g, '$1');
    
    // Step 2: Remove technical markers
    text = text.replace(/[·•]\s*pause\s*rythmique/gi, '');
    text = text.replace(/pause\s*rythmique/gi, '');
    text = text.replace(/[·•]/g, '');
    
    // Step 3: Normalize whitespace
    text = text.replace(/[\r\n]+/g, ' ');
    text = text.replace(/\s{2,}/g, ' ');
    text = text.trim();
    
    if (!text) return [];
    
    // Step 4: Split into segments by Thai sentence-ending particles
    const endingParticles = 'ครับค่ะคะนะสิเลยไหมมาได้แล้ว';
    const segments = [];
    let currentSegment = '';
    const words = text.split(/\s+/);
    
    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      currentSegment += (currentSegment ? ' ' : '') + word;
      
      const lastChar = word.slice(-1);
      const endsWithParticle = endingParticles.includes(lastChar);
      const nextWord = words[i + 1];
      const nextStartsThai = nextWord && /^[ก-ฮ]/.test(nextWord);
      
      if ((endsWithParticle && nextStartsThai) || i === words.length - 1) {
        const trimmed = currentSegment.trim();
        // STRICT: Only add non-empty segments with actual Thai content
        if (trimmed && trimmed.length > 0 && /[ก-ฮ]/.test(trimmed)) {
          segments.push(trimmed);
        }
        currentSegment = '';
      }
    }
    
    // Ensure any remaining text is added
    if (currentSegment.trim() && /[ก-ฮ]/.test(currentSegment.trim())) {
      segments.push(currentSegment.trim());
    }
    
    return segments;
  };
  
  // ============================================================
  // HYBRID AUDIO ARCHITECTURE FOR GLOBAL PLAY
  // - 10/20 words: SINGLE continuous audio
  // - 30/50/100 words: PRELOADED CHUNKS (all generated before playback)
  // ============================================================
  
  // Clean text for TTS (same for both modes)
  // PROMPT 3D: Inclut le nettoyage des labels UI
  const cleanTextForAudio = (thaiText) => {
    if (!thaiText) return '';
    
    let text = thaiText;
    
    // PROMPT 3D: Supprimer les labels UI avant envoi au TTS
    text = stripLabelsForTTS(text);
    
    // Remove special markers <<standard||street>> - keep only standard
    text = text.replace(/<<([^|]+)\|\|[^>]+>>/g, '$1');
    
    // Remove technical markers
    text = text.replace(/[·•]\s*pause\s*rythmique/gi, '');
    text = text.replace(/pause\s*rythmique/gi, '');
    text = text.replace(/[·•]/g, '');
    
    // Replace multiple newlines with single space
    text = text.replace(/[\r\n]+/g, ' ');
    
    // Replace multiple spaces with single space
    text = text.replace(/\s{2,}/g, ' ');
    
    // Trim
    text = text.trim();
    
    return text;
  };
  
  // Split text into chunks for preloaded playback (30/50/100 words)
  const splitTextIntoChunks = (cleanedText) => {
    if (!cleanedText) return [];
    
    const chunks = [];
    const endingParticles = 'ครับค่ะคะนะสิเลยไหมมาได้แล้ว';
    const words = cleanedText.split(/\s+/);
    
    let currentChunk = '';
    let wordCount = 0;
    const MAX_WORDS_PER_CHUNK = 25; // ~25 words per chunk for reliability
    
    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      currentChunk += (currentChunk ? ' ' : '') + word;
      wordCount++;
      
      const lastChar = word.slice(-1);
      const endsWithParticle = endingParticles.includes(lastChar);
      const nextWord = words[i + 1];
      const nextStartsThai = nextWord && /^[ก-ฮ]/.test(nextWord);
      
      // Create chunk at natural boundary or when max words reached
      const isNaturalBreak = endsWithParticle && nextStartsThai;
      const isMaxReached = wordCount >= MAX_WORDS_PER_CHUNK;
      const isLastWord = i === words.length - 1;
      
      if ((isNaturalBreak && wordCount >= 10) || isMaxReached || isLastWord) {
        const trimmed = currentChunk.trim();
        if (trimmed && /[ก-ฮ]/.test(trimmed)) {
          chunks.push(trimmed);
        }
        currentChunk = '';
        wordCount = 0;
      }
    }
    
    // Add any remaining text
    if (currentChunk.trim() && /[ก-ฮ]/.test(currentChunk.trim())) {
      chunks.push(currentChunk.trim());
    }
    
    return chunks;
  };
  
  // Generate a single audio blob with retry - ENHANCED LOGGING
  const generateAudioBlob = async (text, chunkIndex, totalChunks, sessionId) => {
    const maxRetries = 3; // Increased retries
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      // Check session validity
      if (globalAudioSessionRef.current.sessionId !== sessionId) {
        logGlobalAudio(`⛔ [generateAudioBlob] Session invalidated during generation of chunk ${chunkIndex + 1}`);
        return null;
      }
      
      const startTime = Date.now();
      logGlobalAudio(`📥 [generateAudioBlob] START chunk ${chunkIndex + 1}/${totalChunks} (attempt ${attempt}/${maxRetries})`);
      logGlobalAudio(`   Text length: ${text.length} chars`);
      logGlobalAudio(`   Text preview: "${text.substring(0, 50)}..."`);
      
      try {
        const response = await axios.post(
          `${API}/tts?text=${encodeURIComponent(text)}&speed=${voiceSpeed}&context=story`,
          null,
          { responseType: 'blob', timeout: 90000 } // Increased timeout
        );
        
        const duration = Date.now() - startTime;
        logGlobalAudio(`📥 [generateAudioBlob] Response received in ${duration}ms`);
        logGlobalAudio(`   HTTP Status: ${response.status}`);
        logGlobalAudio(`   Response headers: ${JSON.stringify(response.headers || {})}`);
        
        if (!response.data) {
          logGlobalAudio(`⚠️ [generateAudioBlob] NO DATA in response for chunk ${chunkIndex + 1}`);
          if (attempt === maxRetries) return null;
          await new Promise(resolve => setTimeout(resolve, 2000));
          continue;
        }
        
        logGlobalAudio(`   Blob size: ${response.data.size} bytes`);
        logGlobalAudio(`   Blob type: "${response.data.type}"`);
        
        if (response.data.size === 0) {
          logGlobalAudio(`⚠️ [generateAudioBlob] EMPTY BLOB (0 bytes) for chunk ${chunkIndex + 1}`);
          if (attempt === maxRetries) return null;
          await new Promise(resolve => setTimeout(resolve, 2000));
          continue;
        }
        
        if (response.data.size < 1000) {
          logGlobalAudio(`⚠️ [generateAudioBlob] SUSPICIOUSLY SMALL blob (${response.data.size} bytes) - may be error`);
        }
        
        logGlobalAudio(`✅ [generateAudioBlob] SUCCESS chunk ${chunkIndex + 1}/${totalChunks}: ${response.data.size} bytes, type="${response.data.type}"`);
        return response.data;
        
      } catch (err) {
        const duration = Date.now() - startTime;
        logGlobalAudio(`❌ [generateAudioBlob] ERROR chunk ${chunkIndex + 1}, attempt ${attempt} after ${duration}ms`);
        logGlobalAudio(`   Error name: ${err.name}`);
        logGlobalAudio(`   Error message: ${err.message}`);
        logGlobalAudio(`   Error code: ${err.code || 'N/A'}`);
        
        if (err.response) {
          logGlobalAudio(`   HTTP Status: ${err.response.status}`);
          logGlobalAudio(`   Response data: ${JSON.stringify(err.response.data || 'N/A')}`);
        }
        
        if (err.name === 'AxiosError' && err.code === 'ECONNABORTED') {
          logGlobalAudio(`   ⏱️ TIMEOUT - request took too long`);
        }
        
        if (attempt === maxRetries) {
          logGlobalAudio(`❌ [generateAudioBlob] FINAL FAILURE after ${maxRetries} attempts`);
          return null;
        }
        
        logGlobalAudio(`   Waiting 2s before retry...`);
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }
    
    return null;
  };
  
  // MAIN: Play story globally with HYBRID architecture
  const playStoryGlobal = async () => {
    if (!story || !story.story_thai) {
      logGlobalAudio('❌ No story text available');
      toast.error("Pas de texte à lire");
      return;
    }
    
    // STRICT: Don't start if already loading or playing - protect against multiple clicks
    if (globalAudioState === 'loading' || globalAudioState === 'playing') {
      logGlobalAudio(`⛔ Blocked - already in state: ${globalAudioState}`);
      return;
    }
    
    // Create new session with unique request ID
    const sessionId = generateSessionId();
    const requestId = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Determine strategy based on word count
    const isShortStory = wordCount <= 20;
    const strategy = isShortStory ? 'single-audio' : 'preloaded-chunks';
    
    logGlobalAudio(`🎬 NEW SESSION: ${sessionId}`);
    logGlobalAudio(`🔑 Request ID: ${requestId}`);
    logGlobalAudio(`📊 Word count: ${wordCount} → Strategy: ${strategy}`);
    
    // Clean text
    const cleanedText = cleanTextForAudio(story.story_thai);
    
    if (!cleanedText || cleanedText.length === 0) {
      logGlobalAudio('❌ No valid text after cleaning');
      toast.error("Aucun texte audio valide");
      return;
    }
    
    logGlobalAudio(`📝 Text cleaned: ${cleanedText.length} chars`);
    logGlobalAudio(`  Preview: "${cleanedText.substring(0, 80)}..."`);
    
    // CRITICAL: Generate unique key for this story audio
    const storyAudioKey = generateStoryAudioKey(story.story_thai, wordCount, voiceSpeed);
    logGlobalAudio(`🔑 [SYNC] Story Audio Key: ${storyAudioKey}`);
    
    // Initialize session with requestId and audio key
    globalAudioSessionRef.current = {
      sessionId: sessionId,
      requestId: requestId,
      isActive: true,
      mode: strategy,
      preloadedBlobs: [],
      preloadedUrls: [],
      storyAudioKey: storyAudioKey // CRITICAL: Track which story this audio belongs to
    };
    
    // Set state to LOADING immediately
    setGlobalAudioState('loading');
    setIsLoadingStoryAudio(true);
    setIsPlayingStory(false);
    
    // ============================================================
    // STRATEGY 1: SINGLE AUDIO (10/20 words)
    // ============================================================
    if (strategy === 'single-audio') {
      logGlobalAudio(`🔊 [SINGLE-AUDIO] MODE for ${wordCount} words`);
      logGlobalAudio(`   Text to send: ${cleanedText.length} chars`);
      
      // Setup timeout for long loading
      const loadingTimeout = setTimeout(() => {
        logGlobalAudio(`⏱️ [SINGLE-AUDIO] TIMEOUT CHECK at 90s - requestId=${requestId}, currentRequestId=${globalAudioSessionRef.current.requestId}`);
        if (globalAudioSessionRef.current.requestId === requestId) {
          logGlobalAudio(`⏱️ [SINGLE-AUDIO] TIMEOUT TRIGGERED - calling cleanup`);
          cleanupGlobalAudio('audio_error');
          toast.error("Chargement trop long. Réessayez.");
        }
      }, 90000);
      
      const ttsStartTime = Date.now();
      
      try {
        logGlobalAudio(`📥 [SINGLE-AUDIO] Sending TTS request...`);
        logGlobalAudio(`   URL: ${API}/tts`);
        logGlobalAudio(`   Text length: ${cleanedText.length} chars`);
        logGlobalAudio(`   Speed: ${voiceSpeed}`);
        
        const response = await axios.post(
          `${API}/tts?text=${encodeURIComponent(cleanedText)}&speed=${voiceSpeed}&context=story`,
          null,
          { responseType: 'blob', timeout: 120000 }
        );
        
        const ttsDuration = Date.now() - ttsStartTime;
        logGlobalAudio(`📥 [SINGLE-AUDIO] TTS response received in ${ttsDuration}ms`);
        logGlobalAudio(`   HTTP Status: ${response.status}`);
        
        // Check if this request is still valid (protect against stale promises)
        if (globalAudioSessionRef.current.requestId !== requestId) {
          logGlobalAudio(`⛔ [SINGLE-AUDIO] Request ${requestId} is STALE (current: ${globalAudioSessionRef.current.requestId})`);
          clearTimeout(loadingTimeout);
          return;
        }
        
        if (!response.data) {
          logGlobalAudio(`❌ [SINGLE-AUDIO] NO DATA in response`);
          clearTimeout(loadingTimeout);
          cleanupGlobalAudio('audio_error');
          toast.error("Erreur audio - pas de données");
          return;
        }
        
        logGlobalAudio(`   Blob size: ${response.data.size} bytes`);
        logGlobalAudio(`   Blob type: "${response.data.type}"`);
        
        if (response.data.size === 0) {
          logGlobalAudio(`❌ [SINGLE-AUDIO] EMPTY BLOB (0 bytes)`);
          clearTimeout(loadingTimeout);
          cleanupGlobalAudio('audio_error');
          toast.error("Erreur audio - réponse vide");
          return;
        }
        
        logGlobalAudio(`✅ [SINGLE-AUDIO] Blob received: ${response.data.size} bytes, type="${response.data.type}"`);
        
        // Create audio URL
        const audioUrl = URL.createObjectURL(response.data);
        logGlobalAudio(`   Audio URL created: ${audioUrl.substring(0, 50)}...`);
        globalAudioSessionRef.current.preloadedUrls = [audioUrl];
        
        // Create audio element
        const audio = new Audio();
        globalAudioRef.current = audio;
        logGlobalAudio(`   Audio element created`);
        
        // CRITICAL: Wait for audio to be TRULY ready before playing
        logGlobalAudio(`⏳ [SINGLE-AUDIO] Waiting for canplaythrough event...`);
        
        await new Promise((resolve, reject) => {
          let resolved = false;
          
          // Timeout for audio loading
          const audioReadyTimeout = setTimeout(() => {
            if (!resolved) {
              resolved = true;
              logGlobalAudio(`⏱️ [SINGLE-AUDIO] canplaythrough TIMEOUT (30s) - audio not ready`);
              logGlobalAudio(`   Audio readyState: ${audio.readyState}`);
              logGlobalAudio(`   Audio networkState: ${audio.networkState}`);
              logGlobalAudio(`   Audio error: ${audio.error ? audio.error.message : 'none'}`);
              reject(new Error('Audio loading timeout'));
            }
          }, 30000);
          
          // When audio is ready to play through
          audio.oncanplaythrough = () => {
            if (!resolved) {
              resolved = true;
              clearTimeout(audioReadyTimeout);
              logGlobalAudio(`✅ [SINGLE-AUDIO] canplaythrough EVENT received`);
              logGlobalAudio(`   Audio duration: ${audio.duration}s`);
              logGlobalAudio(`   Audio readyState: ${audio.readyState}`);
              resolve();
            }
          };
          
          // Also listen to canplay as fallback
          audio.oncanplay = () => {
            logGlobalAudio(`   [SINGLE-AUDIO] canplay event (readyState: ${audio.readyState})`);
          };
          
          audio.onloadedmetadata = () => {
            logGlobalAudio(`   [SINGLE-AUDIO] loadedmetadata (duration: ${audio.duration}s)`);
          };
          
          // Handle loading errors
          audio.onerror = (e) => {
            if (!resolved) {
              resolved = true;
              clearTimeout(audioReadyTimeout);
              const errorCode = audio.error ? audio.error.code : 'unknown';
              const errorMsg = audio.error ? audio.error.message : 'unknown';
              logGlobalAudio(`❌ [SINGLE-AUDIO] Audio loading ERROR`);
              logGlobalAudio(`   Error event type: ${e.type}`);
              logGlobalAudio(`   Audio error code: ${errorCode}`);
              logGlobalAudio(`   Audio error message: ${errorMsg}`);
              reject(new Error(`Audio loading error: ${errorMsg}`));
            }
          };
          
          // Start loading
          audio.src = audioUrl;
          logGlobalAudio(`   [SINGLE-AUDIO] audio.src set, calling load()...`);
          audio.load();
        });
        
        // Clear loading timeout since audio is ready
        clearTimeout(loadingTimeout);
        logGlobalAudio(`✅ [SINGLE-AUDIO] Audio fully loaded and ready`);
        
        // Check again if request is still valid
        if (globalAudioSessionRef.current.requestId !== requestId) {
          logGlobalAudio(`⛔ [SINGLE-AUDIO] Request became STALE during audio loading`);
          try { URL.revokeObjectURL(audioUrl); } catch (e) {}
          return;
        }
        
        // Setup playback event handlers
        audio.onended = () => {
          if (globalAudioSessionRef.current.requestId !== requestId) {
            logGlobalAudio(`🎵 [SINGLE-AUDIO] 'ended' but request ${requestId} is stale`);
            return;
          }
          logGlobalAudio(`🎵 [SINGLE-AUDIO] PLAYBACK COMPLETE (ended event)`);
          logGlobalAudio(`🎉 SUCCESS: Entire story played!`);
          cleanupGlobalAudio('playback_complete');
        };
        
        audio.onerror = (e) => {
          if (globalAudioSessionRef.current.requestId !== requestId) return;
          const errorCode = audio.error ? audio.error.code : 'unknown';
          const errorMsg = audio.error ? audio.error.message : 'unknown';
          logGlobalAudio(`❌ [SINGLE-AUDIO] Playback ERROR`);
          logGlobalAudio(`   Error type: ${e.type}`);
          logGlobalAudio(`   Error code: ${errorCode}`);
          logGlobalAudio(`   Error message: ${errorMsg}`);
          cleanupGlobalAudio('audio_error');
          toast.error("Erreur de lecture audio");
        };
        
        // NOW start playing - audio is guaranteed ready
        logGlobalAudio(`▶️ [SINGLE-AUDIO] Calling audio.play()...`);
        logGlobalAudio(`   Current globalAudioState: ${globalAudioState}`);
        
        try {
          await audio.play();
          logGlobalAudio(`✅ [SINGLE-AUDIO] audio.play() SUCCESS`);
          
          // Only NOW switch to playing state
          logGlobalAudio(`   Setting state to 'playing'...`);
          setGlobalAudioState('playing');
          setIsPlayingStory(true);
          setIsLoadingStoryAudio(false);
          
          logGlobalAudio(`🔊 [SINGLE-AUDIO] Now playing (duration: ${audio.duration}s)`);
        } catch (playError) {
          logGlobalAudio(`❌ [SINGLE-AUDIO] audio.play() FAILED`);
          logGlobalAudio(`   Error name: ${playError.name}`);
          logGlobalAudio(`   Error message: ${playError.message}`);
          
          if (playError.name === 'NotAllowedError') {
            logGlobalAudio(`   ⚠️ NotAllowedError - user interaction required`);
            toast.error("Cliquez à nouveau pour lire l'audio");
          } else if (playError.name === 'AbortError') {
            logGlobalAudio(`   ⚠️ AbortError - playback was aborted`);
          }
          
          cleanupGlobalAudio('audio_error');
          toast.error("Erreur de lecture audio. Réessayez.");
          return;
        }
        
      } catch (err) {
        clearTimeout(loadingTimeout);
        const ttsDuration = Date.now() - ttsStartTime;
        
        // Check if request is still valid
        if (globalAudioSessionRef.current.requestId !== requestId) {
          logGlobalAudio(`⛔ [SINGLE-AUDIO] Error but request is stale, ignoring`);
          return;
        }
        
        logGlobalAudio(`❌ [SINGLE-AUDIO] CATCH BLOCK ERROR after ${ttsDuration}ms`);
        logGlobalAudio(`   Error name: ${err.name}`);
        logGlobalAudio(`   Error message: ${err.message}`);
        logGlobalAudio(`   Error code: ${err.code || 'N/A'}`);
        
        if (err.response) {
          logGlobalAudio(`   HTTP Status: ${err.response.status}`);
          logGlobalAudio(`   Response data: ${JSON.stringify(err.response.data || 'N/A')}`);
        }
        
        if (err.code === 'ECONNABORTED') {
          logGlobalAudio(`   ⏱️ Request TIMEOUT (axios)`);
          toast.error("Délai dépassé. Réessayez.");
        } else {
          toast.error("Erreur de génération audio. Réessayez.");
        }
        
        cleanupGlobalAudio('audio_error');
      }
      
      return;
    }
    
    // ============================================================
    // STRATEGY 2: PRELOADED CHUNKS (30/50/100 words)
    // ============================================================
    logGlobalAudio(`🔊 [PRELOADED-CHUNKS] MODE for ${wordCount} words`);
    logGlobalAudio(`   Text length: ${cleanedText.length} chars`);
    logGlobalAudio(`   Session: ${sessionId}`);
    logGlobalAudio(`   Request: ${requestId}`);
    
    // WRAP ENTIRE PRELOADED-CHUNKS LOGIC IN TRY-CATCH
    try {
      // Split into chunks
      logGlobalAudio(`📦 [PRELOADED-CHUNKS] Splitting text into chunks...`);
      const chunks = splitTextIntoChunks(cleanedText);
      
      if (!chunks || chunks.length === 0) {
        logGlobalAudio('❌ [PRELOADED-CHUNKS] No valid chunks after split - chunks is empty or null');
        logGlobalAudio(`   chunks value: ${JSON.stringify(chunks)}`);
        cleanupGlobalAudio('audio_error');
        toast.error("Aucun bloc audio valide");
        return;
      }
      
      logGlobalAudio(`📦 [PRELOADED-CHUNKS] Created ${chunks.length} chunks:`);
      chunks.forEach((chunk, i) => {
        logGlobalAudio(`   Chunk ${i + 1}: ${chunk ? chunk.length : 'NULL'} chars - "${chunk ? chunk.substring(0, 50) : 'NULL'}..."`);
      });
      
      // Validate all chunks before proceeding
      for (let i = 0; i < chunks.length; i++) {
        if (!chunks[i] || chunks[i].length === 0) {
          logGlobalAudio(`❌ [PRELOADED-CHUNKS] INVALID chunk at index ${i} - null or empty`);
          cleanupGlobalAudio('audio_error');
          toast.error(`Bloc audio ${i + 1} invalide`);
          return;
        }
      }
      
      // PRELOAD ALL CHUNKS BEFORE PLAYBACK
      logGlobalAudio(`⏳ [PRELOADED-CHUNKS] Starting preload of ${chunks.length} chunks...`);
      const preloadStartTime = Date.now();
      
      const preloadedBlobs = [];
      const preloadedUrls = [];
      
      for (let i = 0; i < chunks.length; i++) {
        logGlobalAudio(`📥 [PRELOADED-CHUNKS] === CHUNK ${i + 1}/${chunks.length} ===`);
        
        // Check session BEFORE generating
        if (!globalAudioSessionRef.current || globalAudioSessionRef.current.sessionId !== sessionId) {
          logGlobalAudio(`⛔ [PRELOADED-CHUNKS] Session INVALIDATED BEFORE generating chunk ${i + 1}`);
          logGlobalAudio(`   Expected sessionId: ${sessionId}`);
          logGlobalAudio(`   Current ref: ${JSON.stringify(globalAudioSessionRef.current)}`);
          // Cleanup any already created URLs
          preloadedUrls.forEach(url => { try { URL.revokeObjectURL(url); } catch (e) {} });
          logGlobalAudio(`   ⚠️ EARLY EXIT: Session invalidated - NOT calling cleanup (already invalid)`);
          return;
        }
        
        const chunkStartTime = Date.now();
        logGlobalAudio(`   Calling generateAudioBlob for chunk ${i + 1}...`);
        
        let blob = null;
        try {
          blob = await generateAudioBlob(chunks[i], i, chunks.length, sessionId);
        } catch (genError) {
          logGlobalAudio(`❌ [PRELOADED-CHUNKS] EXCEPTION in generateAudioBlob for chunk ${i + 1}`);
          logGlobalAudio(`   Error: ${genError.name}: ${genError.message}`);
          logGlobalAudio(`   Stack: ${genError.stack}`);
          preloadedUrls.forEach(url => { try { URL.revokeObjectURL(url); } catch (e) {} });
          cleanupGlobalAudio('audio_error');
          toast.error(`Erreur génération bloc ${i + 1}`);
          return;
        }
        
        const chunkDuration = Date.now() - chunkStartTime;
        
        if (!blob) {
          logGlobalAudio(`❌ [PRELOADED-CHUNKS] generateAudioBlob returned NULL for chunk ${i + 1} after ${chunkDuration}ms`);
          logGlobalAudio(`   Cleaning up ${preloadedUrls.length} already created URLs`);
          preloadedUrls.forEach(url => { try { URL.revokeObjectURL(url); } catch (e) {} });
          cleanupGlobalAudio('audio_error');
          toast.error(`Erreur de génération audio (bloc ${i + 1}/${chunks.length})`);
          return;
        }
        
        if (blob.size === 0) {
          logGlobalAudio(`❌ [PRELOADED-CHUNKS] Blob has SIZE 0 for chunk ${i + 1}`);
          preloadedUrls.forEach(url => { try { URL.revokeObjectURL(url); } catch (e) {} });
          cleanupGlobalAudio('audio_error');
          toast.error(`Blob audio vide (bloc ${i + 1})`);
          return;
        }
        
        let url = null;
        try {
          url = URL.createObjectURL(blob);
        } catch (urlError) {
          logGlobalAudio(`❌ [PRELOADED-CHUNKS] Failed to create ObjectURL for chunk ${i + 1}`);
          logGlobalAudio(`   Error: ${urlError.name}: ${urlError.message}`);
          preloadedUrls.forEach(u => { try { URL.revokeObjectURL(u); } catch (e) {} });
          cleanupGlobalAudio('audio_error');
          toast.error(`Erreur URL audio (bloc ${i + 1})`);
          return;
        }
        
        if (!url) {
          logGlobalAudio(`❌ [PRELOADED-CHUNKS] createObjectURL returned NULL for chunk ${i + 1}`);
          preloadedUrls.forEach(u => { try { URL.revokeObjectURL(u); } catch (e) {} });
          cleanupGlobalAudio('audio_error');
          toast.error(`URL audio nulle (bloc ${i + 1})`);
          return;
        }
        
        preloadedBlobs.push(blob);
        preloadedUrls.push(url);
        
        logGlobalAudio(`✅ [PRELOADED-CHUNKS] Chunk ${i + 1}/${chunks.length} READY in ${chunkDuration}ms`);
        logGlobalAudio(`   Blob: size=${blob.size} bytes, type="${blob.type}"`);
        logGlobalAudio(`   URL: ${url.substring(0, 50)}...`);
        
        // Check session AFTER generating
        if (!globalAudioSessionRef.current || globalAudioSessionRef.current.sessionId !== sessionId) {
          logGlobalAudio(`⛔ [PRELOADED-CHUNKS] Session INVALIDATED AFTER generating chunk ${i + 1}`);
          preloadedUrls.forEach(u => { try { URL.revokeObjectURL(u); } catch (e) {} });
          logGlobalAudio(`   ⚠️ EARLY EXIT: Session invalidated after chunk generation`);
          return;
        }
      }
      
      const totalPreloadTime = Date.now() - preloadStartTime;
      logGlobalAudio(`✅ [PRELOADED-CHUNKS] === ALL ${chunks.length} CHUNKS PRELOADED ===`);
      logGlobalAudio(`   Total time: ${totalPreloadTime}ms`);
      logGlobalAudio(`   Total blobs: ${preloadedBlobs.length}`);
      logGlobalAudio(`   Total URLs: ${preloadedUrls.length}`);
      
      // Validate arrays
      if (preloadedBlobs.length !== chunks.length || preloadedUrls.length !== chunks.length) {
        logGlobalAudio(`❌ [PRELOADED-CHUNKS] MISMATCH: expected ${chunks.length} chunks but got ${preloadedBlobs.length} blobs and ${preloadedUrls.length} URLs`);
        preloadedUrls.forEach(u => { try { URL.revokeObjectURL(u); } catch (e) {} });
        cleanupGlobalAudio('audio_error');
        toast.error("Erreur de préparation audio");
        return;
      }
      
      // Store in session ref
      globalAudioSessionRef.current.preloadedBlobs = preloadedBlobs;
      globalAudioSessionRef.current.preloadedUrls = preloadedUrls;
      
      // Final session check before starting playback
      if (!globalAudioSessionRef.current || globalAudioSessionRef.current.sessionId !== sessionId) {
        logGlobalAudio(`⛔ [PRELOADED-CHUNKS] Session INVALIDATED before playback start`);
        preloadedUrls.forEach(url => { try { URL.revokeObjectURL(url); } catch (e) {} });
        logGlobalAudio(`   ⚠️ EARLY EXIT: Not calling cleanup (session already invalid)`);
        return;
      }
      
      // ============================================================
      // START PLAYBACK - switch to "Arrêter" state
      // ============================================================
      logGlobalAudio(`▶️ [PRELOADED-CHUNKS] === STARTING PLAYBACK ===`);
      logGlobalAudio(`   Current globalAudioState: ${globalAudioState}`);
      logGlobalAudio(`   Setting state to 'playing'...`);
      
      setGlobalAudioState('playing');
      setIsPlayingStory(true);
      setIsLoadingStoryAudio(false);
      
      logGlobalAudio(`   ✅ State set to 'playing'`);
      
      // Play chunks sequentially (NO TTS calls during playback)
      let currentChunkIndex = 0;
      let playbackStarted = false;
      
      const playNextChunk = () => {
        logGlobalAudio(`🔄 [PRELOADED-CHUNKS] playNextChunk() - index: ${currentChunkIndex}/${preloadedUrls.length}`);
        
        // Check session validity
        if (!globalAudioSessionRef.current || globalAudioSessionRef.current.sessionId !== sessionId) {
          logGlobalAudio(`⛔ [PRELOADED-CHUNKS] Session INVALIDATED during playback at chunk ${currentChunkIndex}`);
          logGlobalAudio(`   ⚠️ NOT calling cleanup - session already invalid`);
          return;
        }
        
        if (currentChunkIndex >= preloadedUrls.length) {
          logGlobalAudio(`🎵 [PRELOADED-CHUNKS] ALL ${chunks.length} chunks played!`);
          logGlobalAudio(`🎉 SUCCESS: Entire story completed!`);
          cleanupGlobalAudio('playback_complete');
          return;
        }
        
        const chunkUrl = preloadedUrls[currentChunkIndex];
        
        if (!chunkUrl) {
          logGlobalAudio(`❌ [PRELOADED-CHUNKS] chunkUrl is NULL at index ${currentChunkIndex}`);
          cleanupGlobalAudio('audio_error');
          toast.error("URL audio manquante");
          return;
        }
        
        logGlobalAudio(`   Creating Audio element for chunk ${currentChunkIndex + 1}...`);
        
        let audio;
        try {
          audio = new Audio();
        } catch (audioCreateError) {
          logGlobalAudio(`❌ [PRELOADED-CHUNKS] Failed to create Audio element`);
          logGlobalAudio(`   Error: ${audioCreateError.message}`);
          cleanupGlobalAudio('audio_error');
          toast.error("Erreur création audio");
          return;
        }
        
        globalAudioRef.current = audio;
        
        audio.onended = () => {
          logGlobalAudio(`🎵 [PRELOADED-CHUNKS] Chunk ${currentChunkIndex + 1}/${chunks.length} ENDED`);
          if (!globalAudioSessionRef.current || globalAudioSessionRef.current.sessionId !== sessionId) {
            logGlobalAudio(`   Session stale - stopping`);
            return;
          }
          currentChunkIndex++;
          playNextChunk();
        };
        
        audio.onerror = (e) => {
          const errorCode = audio.error ? audio.error.code : 'unknown';
          const errorMsg = audio.error ? audio.error.message : 'unknown';
          logGlobalAudio(`❌ [PRELOADED-CHUNKS] audio.onerror for chunk ${currentChunkIndex + 1}`);
          logGlobalAudio(`   Event type: ${e ? e.type : 'N/A'}`);
          logGlobalAudio(`   Error code: ${errorCode}`);
          logGlobalAudio(`   Error message: ${errorMsg}`);
          
          currentChunkIndex++;
          if (currentChunkIndex < preloadedUrls.length) {
            logGlobalAudio(`   Trying next chunk...`);
            playNextChunk();
          } else {
            logGlobalAudio(`   No more chunks - cleanup`);
            cleanupGlobalAudio('audio_error');
            toast.error("Erreur lecture audio");
          }
        };
        
        logGlobalAudio(`   Setting audio.src to: ${chunkUrl.substring(0, 50)}...`);
        audio.src = chunkUrl;
        
        logGlobalAudio(`▶️ [PRELOADED-CHUNKS] Calling audio.play() for chunk ${currentChunkIndex + 1}...`);
        
        audio.play()
          .then(() => {
            playbackStarted = true;
            logGlobalAudio(`✅ [PRELOADED-CHUNKS] Chunk ${currentChunkIndex + 1} play() SUCCESS`);
            logGlobalAudio(`   Duration: ${audio.duration}s`);
            logGlobalAudio(`   Playback has started: ${playbackStarted}`);
          })
          .catch(err => {
            logGlobalAudio(`❌ [PRELOADED-CHUNKS] Chunk ${currentChunkIndex + 1} play() REJECTED`);
            logGlobalAudio(`   Error name: ${err.name}`);
            logGlobalAudio(`   Error message: ${err.message}`);
            
            if (err.name === 'NotAllowedError') {
              // SAFARI AUTOPLAY BLOCKED - DO NOT CLEANUP! Keep preloaded chunks.
              logGlobalAudio(`   ⚠️ NotAllowedError - Safari autoplay blocked`);
              logGlobalAudio(`   🔒 KEEPING preloaded chunks intact (${preloadedUrls.length} URLs)`);
              logGlobalAudio(`   🔒 Storing current chunk index: ${currentChunkIndex}`);
              logGlobalAudio(`   🔄 Switching to 'audio_ready' state - waiting for user tap`);
              
              // Store the chunk index for resumption
              globalAudioSessionRef.current.resumeChunkIndex = currentChunkIndex;
              globalAudioSessionRef.current.chunksForResume = chunks;
              
              // Switch to audio_ready state - DO NOT CALL CLEANUP
              setGlobalAudioState('audio_ready');
              setIsPlayingStory(false);
              setIsLoadingStoryAudio(false);
              
              // NO toast.error - this is not an error, just Safari policy
              logGlobalAudio(`   ✅ State set to 'audio_ready' - user can tap to play`);
              return;
            }
            
            if (err.name === 'AbortError') {
              logGlobalAudio(`   ⚠️ AbortError - play was interrupted`);
            }
            
            currentChunkIndex++;
            if (currentChunkIndex < preloadedUrls.length) {
              logGlobalAudio(`   Trying next chunk...`);
              playNextChunk();
            } else {
              logGlobalAudio(`   No more chunks - cleanup`);
              cleanupGlobalAudio('audio_error');
              toast.error("Erreur de lecture audio");
            }
          });
      };
      
      // Start playing first chunk
      logGlobalAudio(`▶️ [PRELOADED-CHUNKS] Calling playNextChunk() for first chunk`);
      playNextChunk();
      
    } catch (outerError) {
      // GLOBAL CATCH FOR ANY UNCAUGHT ERROR IN PRELOADED-CHUNKS STRATEGY
      logGlobalAudio(`🚨🚨🚨 [PRELOADED-CHUNKS] UNCAUGHT EXCEPTION 🚨🚨🚨`);
      logGlobalAudio(`   Error name: ${outerError.name}`);
      logGlobalAudio(`   Error message: ${outerError.message}`);
      logGlobalAudio(`   Error stack: ${outerError.stack}`);
      logGlobalAudio(`   Current state before cleanup: ${globalAudioState}`);
      
      // Make sure we clean up and show error
      try {
        cleanupGlobalAudio('audio_error');
      } catch (cleanupErr) {
        logGlobalAudio(`   ⚠️ Cleanup also failed: ${cleanupErr.message}`);
        // Force reset state
        setGlobalAudioState('idle');
        setIsPlayingStory(false);
        setIsLoadingStoryAudio(false);
      }
      
      toast.error("Erreur inattendue. Réessayez.");
    }
  };
  
  // Stop global audio playback
  const stopStoryGlobal = () => {
    logGlobalAudio('🛑 User requested STOP');
    setGlobalAudioState('stopping');
    cleanupGlobalAudio('user_stop');
  };

  // ============================================================
  // RESUME PRELOADED AUDIO - Called when user taps "Audio prêt — Lire"
  // This uses already preloaded chunks - NO new TTS calls
  // ============================================================
  const resumePreloadedAudio = () => {
    logGlobalAudio(`▶️ [RESUME] User tapped "Audio prêt — Lire"`);
    logGlobalAudio(`   Current state: ${globalAudioState}`);
    
    // Verify we have preloaded data
    if (!globalAudioSessionRef.current) {
      logGlobalAudio(`❌ [RESUME] No session ref - falling back to full playback`);
      playStoryGlobal();
      return;
    }
    
    // CRITICAL: Verify audio key matches currently displayed story
    const storedAudioKey = globalAudioSessionRef.current.storyAudioKey;
    const currentAudioKey = story ? generateStoryAudioKey(story.story_thai, wordCount, voiceSpeed) : null;
    
    logGlobalAudio(`🔑 [SYNC] Stored audio key: ${storedAudioKey}`);
    logGlobalAudio(`🔑 [SYNC] Current story key: ${currentAudioKey}`);
    
    if (!storedAudioKey || !currentAudioKey || storedAudioKey !== currentAudioKey) {
      logGlobalAudio(`❌ [SYNC] AUDIO KEY MISMATCH - Audio belongs to DIFFERENT story!`);
      logGlobalAudio(`   🚫 Refusing to play old audio on new story`);
      logGlobalAudio(`   🧹 Cleaning up obsolete audio and regenerating`);
      cleanupGlobalAudio('new_story');
      toast.info("Histoire modifiée - rechargement audio...");
      // Start fresh playback for the current story
      setTimeout(() => playStoryGlobal(), 100);
      return;
    }
    
    logGlobalAudio(`✅ [SYNC] Audio key MATCHES - safe to play`);
    
    const preloadedUrls = globalAudioSessionRef.current.preloadedUrls;
    const resumeIndex = globalAudioSessionRef.current.resumeChunkIndex || 0;
    const chunks = globalAudioSessionRef.current.chunksForResume;
    const sessionId = globalAudioSessionRef.current.sessionId;
    
    logGlobalAudio(`   Session ID: ${sessionId}`);
    logGlobalAudio(`   Preloaded URLs: ${preloadedUrls ? preloadedUrls.length : 0}`);
    logGlobalAudio(`   Resume index: ${resumeIndex}`);
    logGlobalAudio(`   Chunks count: ${chunks ? chunks.length : 0}`);
    
    if (!preloadedUrls || preloadedUrls.length === 0) {
      logGlobalAudio(`❌ [RESUME] No preloaded URLs found - falling back to full playback`);
      cleanupGlobalAudio('audio_error');
      playStoryGlobal();
      return;
    }
    
    if (resumeIndex >= preloadedUrls.length) {
      logGlobalAudio(`❌ [RESUME] Resume index out of bounds - falling back to full playback`);
      cleanupGlobalAudio('audio_error');
      playStoryGlobal();
      return;
    }
    
    logGlobalAudio(`✅ [RESUME] Using existing preloaded chunks - NO new TTS calls`);
    logGlobalAudio(`   Starting from chunk ${resumeIndex + 1}/${preloadedUrls.length}`);
    
    // Switch to playing state
    setGlobalAudioState('playing');
    setIsPlayingStory(true);
    setIsLoadingStoryAudio(false);
    
    // Play chunks sequentially starting from resumeIndex
    let currentChunkIndex = resumeIndex;
    
    const playNextChunkResume = () => {
      logGlobalAudio(`🔄 [RESUME] playNextChunkResume() - index: ${currentChunkIndex}/${preloadedUrls.length}`);
      
      // Check session validity
      if (!globalAudioSessionRef.current || globalAudioSessionRef.current.sessionId !== sessionId) {
        logGlobalAudio(`⛔ [RESUME] Session INVALIDATED during playback`);
        return;
      }
      
      if (currentChunkIndex >= preloadedUrls.length) {
        logGlobalAudio(`🎵 [RESUME] ALL chunks played!`);
        logGlobalAudio(`🎉 SUCCESS: Entire story completed via resume!`);
        cleanupGlobalAudio('playback_complete');
        return;
      }
      
      const chunkUrl = preloadedUrls[currentChunkIndex];
      
      if (!chunkUrl) {
        logGlobalAudio(`❌ [RESUME] chunkUrl is NULL at index ${currentChunkIndex}`);
        cleanupGlobalAudio('audio_error');
        toast.error("URL audio manquante");
        return;
      }
      
      const audio = new Audio();
      globalAudioRef.current = audio;
      
      audio.onended = () => {
        logGlobalAudio(`🎵 [RESUME] Chunk ${currentChunkIndex + 1}/${preloadedUrls.length} ENDED`);
        if (!globalAudioSessionRef.current || globalAudioSessionRef.current.sessionId !== sessionId) {
          return;
        }
        currentChunkIndex++;
        playNextChunkResume();
      };
      
      audio.onerror = (e) => {
        const errorCode = audio.error ? audio.error.code : 'unknown';
        const errorMsg = audio.error ? audio.error.message : 'unknown';
        logGlobalAudio(`❌ [RESUME] audio.onerror for chunk ${currentChunkIndex + 1}`);
        logGlobalAudio(`   Error code: ${errorCode}, message: ${errorMsg}`);
        
        currentChunkIndex++;
        if (currentChunkIndex < preloadedUrls.length) {
          playNextChunkResume();
        } else {
          cleanupGlobalAudio('audio_error');
          toast.error("Erreur lecture audio");
        }
      };
      
      audio.src = chunkUrl;
      logGlobalAudio(`▶️ [RESUME] Calling audio.play() for chunk ${currentChunkIndex + 1}...`);
      
      audio.play()
        .then(() => {
          logGlobalAudio(`✅ [RESUME] Chunk ${currentChunkIndex + 1} play() SUCCESS (duration: ${audio.duration}s)`);
        })
        .catch(err => {
          logGlobalAudio(`❌ [RESUME] Chunk ${currentChunkIndex + 1} play() FAILED`);
          logGlobalAudio(`   Error: ${err.name}: ${err.message}`);
          
          // If still NotAllowedError after user tap, something is really wrong
          if (err.name === 'NotAllowedError') {
            logGlobalAudio(`   ⚠️ Still NotAllowedError after user tap - real error`);
            cleanupGlobalAudio('audio_error');
            toast.error("Safari bloque l'audio. Réessayez.");
            return;
          }
          
          currentChunkIndex++;
          if (currentChunkIndex < preloadedUrls.length) {
            playNextChunkResume();
          } else {
            cleanupGlobalAudio('audio_error');
            toast.error("Erreur de lecture audio");
          }
        });
    };
    
    // Start playing from resume index
    logGlobalAudio(`▶️ [RESUME] Starting playback from chunk ${resumeIndex + 1}`);
    playNextChunkResume();
  };

  // State for audio preparation status (kept for compatibility)
  const [audioPreparationStatus, setAudioPreparationStatus] = useState('');

  // Legacy function aliases for compatibility
  const playStory = playStoryGlobal;
  const stopStory = stopStoryGlobal;

  const getSpeedLabel = (speed) => {
    if (speed <= 0.35) return "Très lent";
    if (speed <= 0.5) return "Lent";
    if (speed <= 0.7) return "Modéré";
    if (speed <= 0.85) return "Confortable";
    return "Normal";
  };

  const getStoryLengthHint = (count) => {
    switch(count) {
      case 10: return "~3-4 phrases - Micro-histoire";
      case 20: return "~5-8 phrases - Scène courte";
      case 30: return "~10-15 phrases - Scène moyenne";
      case 50: return "~15-25 phrases - Scène développée";
      case 100: return "~30-50 phrases - Scène longue";
      default: return "";
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => { stopStory(); onClose(); }}>
      <DialogContent className="sm:max-w-5xl lg:max-w-6xl bg-[#FDFBF7] border-none max-h-[95vh] sm:max-h-[90vh] overflow-y-auto px-3 sm:px-6">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <BookText className="w-6 h-6 text-amber-500" />
            {mode === "story" ? "Générateur d'histoires" : "Phrases quotidiennes"}
          </DialogTitle>
          <DialogDescription>
            {mode === "story" 
              ? "Générez une histoire en thaï parlé de Bangkok avec des mots aléatoires"
              : "Générez des phrases courantes pour la pratique quotidienne"}
          </DialogDescription>
        </DialogHeader>

        {/* Mode selector */}
        <div className="flex gap-2 p-1 bg-slate-100 rounded-xl">
          <button
            onClick={() => {
              cleanupPhraseAudio(); // Stop audio when switching modes
              setMode("story");
            }}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
              mode === "story" 
                ? "bg-white shadow text-amber-600" 
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <BookOpen className="w-4 h-4" />
            Scènes du quotidien
          </button>
          <button
            onClick={() => {
              cleanupPhraseAudio(); // Stop audio when switching modes
              setMode("phrases");
            }}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
              mode === "phrases" 
                ? "bg-white shadow text-teal-600" 
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <MessageCircle className="w-4 h-4" />
            Phrases quotidiennes
          </button>
        </div>

        {/* PHRASES MODE - FLUENCY TRAINING */}
        {mode === "phrases" && (
          <div className="space-y-4 mt-4">
            {/* Mode selection: Standard vs Progressive */}
            <div className="flex gap-2 p-1 bg-slate-100 rounded-xl">
              <button
                onClick={() => setPhraseMode("standard")}
                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  phraseMode === "standard" 
                    ? "bg-white shadow text-teal-600" 
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <MessageCircle className="w-4 h-4" />
                Standard
              </button>
              <button
                onClick={() => setPhraseMode("progressive")}
                className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                  phraseMode === "progressive" 
                    ? "bg-white shadow text-purple-600" 
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <Layers className="w-4 h-4" />
                Construction progressive
              </button>
            </div>

            {/* Mode description */}
            {phraseMode === "progressive" && (
              <div className="bg-gradient-to-r from-purple-50 to-violet-50 rounded-xl p-4 border border-purple-200">
                <h4 className="font-medium text-purple-800 mb-2 flex items-center gap-2">
                  <Layers className="w-4 h-4" />
                  Mode Construction Progressive
                </h4>
                <p className="text-sm text-purple-700">
                  Partez d'une phrase simple et ajoutez des éléments étape par étape : 
                  <span className="font-medium"> objet → lieu → temps → description</span>.
                  Parfait pour comprendre comment construire des phrases complexes !
                </p>
              </div>
            )}

            {/* Keyword input for progressive mode */}
            {phraseMode === "progressive" && (
              <div>
                <label className="text-sm font-medium text-slate-600 mb-2 block flex items-center gap-2">
                  <Edit3 className="w-4 h-4 text-purple-500" />
                  Mot-clé de base (optionnel)
                </label>
                <input
                  type="text"
                  value={progressiveKeyword}
                  onChange={(e) => setProgressiveKeyword(e.target.value)}
                  placeholder="Ex: กิน (manger), ซื้อ (acheter), ไป (aller), สวย (beau)..."
                  className="w-full px-4 py-3 border border-purple-200 rounded-xl focus:outline-none focus:border-purple-500 bg-purple-50/50"
                  data-testid="progressive-keyword-input"
                />
                <p className="text-xs text-purple-600 mt-1">
                  Entrez un verbe, nom ou adjectif thaï pour générer des phrases autour de ce mot
                </p>
              </div>
            )}

            {/* Topic selection */}
            <div>
              <label className="text-sm font-medium text-slate-600 mb-2 block flex items-center gap-2">
                <Edit3 className="w-4 h-4" />
                Sujet (optionnel)
              </label>
              <input
                type="text"
                value={phraseTopic}
                onChange={(e) => setPhraseTopic(e.target.value)}
                placeholder="Ex: Au restaurant, Négocier un prix, Demander son chemin..."
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:border-teal-500 mb-3"
                data-testid="phrase-topic-input"
              />
              <div className="flex flex-wrap gap-2">
                {phraseTopicSuggestions.map((t, idx) => (
                  <button
                    key={idx}
                    onClick={() => setPhraseTopic(t.replace(/^[^\s]+\s/, ''))} 
                    className={`px-3 py-1.5 rounded-full text-xs transition-all ${
                      phraseTopic === t.replace(/^[^\s]+\s/, '')
                        ? phraseMode === "progressive"
                          ? 'bg-purple-500 text-white'
                          : 'bg-teal-500 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            {/* Level selection */}
            <div>
              <label className="text-sm font-medium text-slate-600 mb-3 block flex items-center gap-2">
                <GraduationCap className="w-4 h-4" />
                Niveau de difficulté
              </label>
              <div className="grid grid-cols-3 gap-3">
                {phraseLevels.map((level) => (
                  <button
                    key={level.id}
                    onClick={() => setPhraseLevel(level.id)}
                    className={`p-3 rounded-xl text-left transition-all ${
                      phraseLevel === level.id
                        ? `bg-gradient-to-r ${level.color} text-white shadow-lg scale-105`
                        : "bg-white border-2 border-slate-200 hover:border-slate-300"
                    }`}
                  >
                    <div className="text-sm font-bold">{level.label}</div>
                    <div className={`text-xs ${phraseLevel === level.id ? "text-white/80" : "text-slate-500"}`}>
                      {level.desc}
                    </div>
                    <div className={`text-xs mt-1 ${phraseLevel === level.id ? "text-white/70" : "text-slate-400"}`}>
                      {level.detail}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Number of phrases */}
            <div>
              <label className="text-sm font-medium text-slate-600 mb-2 block">
                Nombre de phrases: {phraseCount}
              </label>
              <div className="flex gap-2">
                {[1, 2, 3].map((n) => (
                  <button
                    key={n}
                    onClick={() => setPhraseCount(n)}
                    className={`flex-1 py-2.5 rounded-xl transition-all font-medium ${
                      phraseCount === n
                        ? phraseMode === "progressive"
                          ? "bg-gradient-to-r from-purple-500 to-violet-500 text-white shadow"
                          : "bg-gradient-to-r from-teal-500 to-cyan-500 text-white shadow"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </div>

            {/* Generate button */}
            <Button
              onClick={generatePhrases}
              disabled={isGeneratingPhrases}
              className={`w-full py-6 rounded-2xl text-lg ${
                phraseMode === "progressive"
                  ? "bg-gradient-to-r from-purple-500 to-violet-500 hover:from-purple-600 hover:to-violet-600"
                  : "bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600"
              } text-white`}
              data-testid="generate-phrases-btn"
            >
              {isGeneratingPhrases ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Génération en cours...
                </>
              ) : (
                <>
                  {phraseMode === "progressive" ? <Layers className="w-5 h-5 mr-2" /> : <Sparkles className="w-5 h-5 mr-2" />}
                  Générer {phraseCount} phrases 
                  {phraseMode === "progressive" ? " progressives" : ""}
                </>
              )}
            </Button>

            {/* History toggle */}
            <div className="flex items-center justify-between">
              <button
                onClick={() => {
                  if (!showHistory) fetchPhraseHistory();
                  setShowHistory(!showHistory);
                }}
                className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1"
              >
                <BookOpen className="w-4 h-4" />
                {showHistory ? "Masquer" : "Voir"} l'historique ({phraseHistory.length} phrases mémorisées)
              </button>
              {showHistory && phraseHistory.length > 0 && (
                <button
                  onClick={() => clearHistory("phrases")}
                  className="text-xs text-red-500 hover:text-red-700"
                >
                  Effacer l'historique
                </button>
              )}
            </div>

            {/* History display */}
            {showHistory && phraseHistory.length > 0 && (
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 max-h-48 overflow-y-auto">
                <h4 className="text-sm font-medium text-slate-600 mb-2">
                  Phrases déjà générées (évitées pour ne pas se répéter) :
                </h4>
                <div className="space-y-1">
                  {phraseHistory.map((p, idx) => (
                    <div key={idx} className="text-sm flex items-center gap-2 py-1 border-b border-slate-100 last:border-0">
                      <span className="font-thai text-slate-700">{p.thai}</span>
                      <span className="text-slate-400">-</span>
                      <span className="text-slate-500">{p.french}</span>
                      {p.topic && (
                        <span className="text-xs bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">{p.topic}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Fluency tips */}
            {phrasesData?.fluency_tips && (
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border border-amber-200">
                <h4 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Conseils pour la fluidité
                </h4>
                <ul className="text-sm text-amber-700 space-y-1">
                  {phrasesData.fluency_tips.map((tip, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <span className="text-amber-500">•</span>
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Phrases results */}
            {phrases.length > 0 && (
              <div className="space-y-4 mt-4">
                <h3 className="font-medium text-slate-700 flex items-center gap-2">
                  <MessageCircle className="w-4 h-4 text-teal-500" />
                  {phrases.length} phrases - Niveau {phrasesData?.level_description}
                </h3>
                
                <div className="space-y-4">
                  {phrases.map((phrase, idx) => (
                    <div 
                      key={idx}
                      className={`bg-white rounded-2xl shadow-sm border-2 transition-all overflow-hidden ${
                        selectedPhrase === idx ? "border-teal-400 shadow-lg" : "border-slate-100 hover:border-slate-200"
                      }`}
                    >
                      {/* Main phrase card */}
                      <div 
                        className="p-4 cursor-pointer"
                        onClick={() => setSelectedPhrase(selectedPhrase === idx ? null : idx)}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <p className="font-thai text-2xl text-slate-800 mb-1">{phrase.thai}</p>
                            <p className="text-slate-600 font-medium">{phrase.french}</p>
                            <p className="text-xs text-slate-400 mt-2 italic">{phrase.situation}</p>
                          </div>
                          <div className="flex flex-col gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                togglePhraseAudio(phrase.thai);
                              }}
                              disabled={phraseAudioState === 'loading'}
                              className={`p-2.5 rounded-full transition-all ${
                                phraseAudioState === 'loading' && phraseAudioTextRef.current === phrase.thai
                                  ? 'bg-gray-200 text-gray-400'
                                  : phraseAudioState === 'playing' && phraseAudioTextRef.current === phrase.thai
                                    ? 'bg-teal-500 text-white animate-pulse'
                                    : phraseAudioState === 'paused' && phraseAudioTextRef.current === phrase.thai
                                      ? 'bg-orange-100 text-orange-600'
                                      : 'bg-teal-100 text-teal-600 hover:bg-teal-200'
                              }`}
                              data-testid={`phrase-audio-btn-${idx}`}
                            >
                              {phraseAudioState === 'loading' && phraseAudioTextRef.current === phrase.thai ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                              ) : phraseAudioState === 'playing' && phraseAudioTextRef.current === phrase.thai ? (
                                <Pause className="w-5 h-5" />
                              ) : (
                                <Volume2 className="w-5 h-5" />
                              )}
                            </button>
                            <div className={`p-1 rounded-full ${selectedPhrase === idx ? "bg-teal-100" : "bg-slate-100"}`}>
                              <ChevronDown className={`w-4 h-4 transition-transform ${selectedPhrase === idx ? "rotate-180 text-teal-600" : "text-slate-400"}`} />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Expanded details - Manee explains */}
                      {selectedPhrase === idx && (
                        <div className="border-t border-slate-100 bg-gradient-to-b from-slate-50 to-white">
                          {/* Structure breakdown */}
                          {phrase.structure && (
                            <div className="p-4 border-b border-slate-100">
                              <h4 className="font-medium text-purple-700 mb-3 flex items-center gap-2">
                                <BookOpen className="w-4 h-4" />
                                Structure de la phrase
                              </h4>
                              <div className="bg-purple-50 rounded-xl p-3 mb-3">
                                <p className="text-purple-800 font-mono text-sm">{phrase.structure.pattern}</p>
                                <p className="text-purple-600 text-xs mt-1">{phrase.structure.explanation}</p>
                              </div>
                              {phrase.structure.elements && (
                                <div className="flex flex-wrap gap-2">
                                  {phrase.structure.elements.map((el, i) => (
                                    <div key={i} className="bg-white border border-purple-200 rounded-lg px-3 py-2">
                                      <div className="text-xs text-purple-500 uppercase font-medium">{el.role}</div>
                                      <div className="font-thai text-lg text-slate-800">{el.thai}</div>
                                      <div className="text-xs text-slate-500">{el.french}</div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Word substitutions */}
                          {phrase.substitutions && phrase.substitutions.length > 0 && (
                            <div className="p-4 border-b border-slate-100">
                              <h4 className="font-medium text-blue-700 mb-3 flex items-center gap-2">
                                <Edit3 className="w-4 h-4" />
                                Substitutions - Crée tes propres phrases !
                              </h4>
                              <div className="space-y-3">
                                {phrase.substitutions.map((sub, i) => (
                                  <div key={i} className="bg-blue-50 rounded-xl p-3">
                                    <div className="flex items-center gap-2 mb-2">
                                      <span className="text-xs bg-blue-200 text-blue-800 px-2 py-0.5 rounded-full uppercase font-medium">
                                        {sub.element}
                                      </span>
                                      <span className="font-thai text-blue-800">{sub.current}</span>
                                      <span className="text-blue-400">→</span>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                      {sub.alternatives?.map((alt, j) => (
                                        <button
                                          key={j}
                                          onClick={() => togglePhraseAudio(alt.thai)}
                                          className="bg-white border border-blue-200 rounded-lg px-3 py-1.5 hover:bg-blue-100 transition-all"
                                        >
                                          <span className="font-thai text-blue-800">{alt.thai}</span>
                                          <span className="text-xs text-slate-500 ml-2">({alt.french})</span>
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Speaking exercise */}
                          {phrase.exercise && (
                            <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50">
                              <h4 className="font-medium text-green-700 mb-3 flex items-center gap-2">
                                <Mic className="w-4 h-4" />
                                Exercice de parole spontanée
                              </h4>
                              <div className="bg-white rounded-xl p-4 border border-green-200">
                                <p className="text-green-800 font-medium mb-2">{phrase.exercise.prompt}</p>
                                {phrase.exercise.hint && (
                                  <p className="text-xs text-green-600 mb-3 flex items-center gap-1">
                                    <HelpCircle className="w-3 h-3" />
                                    Indice: {phrase.exercise.hint}
                                  </p>
                                )}
                                
                                {!showExercise || selectedPhrase !== idx ? (
                                  <Button
                                    onClick={() => {
                                      setShowExercise(true);
                                      setExerciseAnswer("");
                                      setExerciseResult(null);
                                    }}
                                    className="bg-green-500 hover:bg-green-600 text-white"
                                    size="sm"
                                  >
                                    Essayer l'exercice
                                  </Button>
                                ) : (
                                  <div className="space-y-3">
                                    <input
                                      type="text"
                                      value={exerciseAnswer}
                                      onChange={(e) => setExerciseAnswer(e.target.value)}
                                      placeholder="Écris ta réponse en thaï..."
                                      className="w-full px-4 py-2 border border-green-200 rounded-xl focus:outline-none focus:border-green-500 font-thai text-lg"
                                    />
                                    <div className="flex gap-2">
                                      <Button
                                        onClick={() => checkExerciseAnswer(phrase)}
                                        className="bg-green-500 hover:bg-green-600 text-white flex-1"
                                        size="sm"
                                      >
                                        <Check className="w-4 h-4 mr-1" />
                                        Vérifier
                                      </Button>
                                      <Button
                                        onClick={() => {
                                          setExerciseResult({
                                            correct: false,
                                            message: `Réponse: ${phrase.exercise.answer}`
                                          });
                                        }}
                                        variant="outline"
                                        className="border-green-300 text-green-700"
                                        size="sm"
                                      >
                                        Voir la réponse
                                      </Button>
                                    </div>
                                    
                                    {exerciseResult && (
                                      <div className={`p-3 rounded-xl ${exerciseResult.correct ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>
                                        <p className="font-medium">{exerciseResult.message}</p>
                                        {!exerciseResult.correct && phrase.exercise.answer && (
                                          <button
                                            onClick={() => togglePhraseAudio(phrase.exercise.answer)}
                                            className="mt-2 flex items-center gap-1 text-sm bg-white px-3 py-1 rounded-lg"
                                          >
                                            <Volume2 className="w-4 h-4" />
                                            Écouter
                                          </button>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Progressive Phrases Results */}
            {progressivePhrases.length > 0 && phraseMode === "progressive" && (
              <div className="space-y-6 mt-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-slate-700 flex items-center gap-2">
                    <Layers className="w-4 h-4 text-purple-500" />
                    {progressivePhrases.length} phrases progressives
                  </h3>
                </div>

                {/* Building tips */}
                {phrasesData?.building_tips && (
                  <div className="bg-gradient-to-r from-purple-50 to-violet-50 rounded-xl p-4 border border-purple-200">
                    <h4 className="font-medium text-purple-800 mb-2 flex items-center gap-2">
                      <Sparkles className="w-4 h-4" />
                      Règles de construction
                    </h4>
                    <ul className="text-sm text-purple-700 space-y-1">
                      {phrasesData.building_tips.map((tip, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <ChevronRight className="w-4 h-4 mt-0.5 text-purple-400" />
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <div className="space-y-6">
                  {progressivePhrases.map((phrase, phraseIdx) => (
                    <div 
                      key={phraseIdx}
                      className="bg-white rounded-2xl shadow-md border-2 border-purple-100 overflow-hidden"
                    >
                      {/* Phrase header */}
                      <div 
                        className="p-4 bg-gradient-to-r from-purple-500 to-violet-500 text-white cursor-pointer"
                        onClick={() => setSelectedProgressivePhrase(selectedProgressivePhrase === phraseIdx ? null : phraseIdx)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="text-purple-200 text-sm">Thème:</span>
                            <h4 className="font-bold text-lg">{phrase.theme}</h4>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className="bg-white/20 text-white">
                              {phrase.steps?.length || 0} étapes
                            </Badge>
                            <ChevronDown className={`w-5 h-5 transition-transform ${selectedProgressivePhrase === phraseIdx ? "rotate-180" : ""}`} />
                          </div>
                        </div>
                      </div>

                      {/* Steps - always visible but collapsible details */}
                      <div className="p-4">
                        {/* Progress visualization */}
                        <div className="relative mb-6">
                          <div className="flex items-center justify-between">
                            {phrase.steps?.map((step, stepIdx) => (
                              <div 
                                key={stepIdx}
                                className="flex flex-col items-center relative z-10"
                                style={{ width: `${100 / (phrase.steps?.length || 1)}%` }}
                              >
                                <div 
                                  className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all cursor-pointer ${
                                    stepIdx === 0
                                      ? "bg-green-500 text-white"
                                      : "bg-purple-500 text-white"
                                  }`}
                                  onClick={() => togglePhraseAudio(step.thai)}
                                >
                                  {stepIdx === 0 ? "▶" : `+${stepIdx}`}
                                </div>
                                <div className="text-xs text-slate-500 mt-1 text-center">
                                  {step.element_type === "base" ? "Base" : step.element_type}
                                </div>
                              </div>
                            ))}
                          </div>
                          {/* Connecting line */}
                          <div className="absolute top-5 left-8 right-8 h-0.5 bg-gradient-to-r from-green-300 via-purple-300 to-purple-500 -z-0" />
                        </div>

                        {/* Steps detail */}
                        <div className="space-y-3">
                          {phrase.steps?.map((step, stepIdx) => (
                            <div 
                              key={stepIdx}
                              className={`rounded-xl p-4 transition-all ${
                                stepIdx === 0 
                                  ? "bg-green-50 border-2 border-green-200" 
                                  : "bg-purple-50 border-2 border-purple-200"
                              }`}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                                      stepIdx === 0 
                                        ? "bg-green-200 text-green-800" 
                                        : "bg-purple-200 text-purple-800"
                                    }`}>
                                      Étape {step.step}
                                    </span>
                                    {step.element_added && (
                                      <span className="text-xs text-purple-600 flex items-center gap-1">
                                        <Plus className="w-3 h-3" />
                                        {step.element_added}
                                      </span>
                                    )}
                                  </div>
                                  <p className="font-thai text-2xl text-slate-800 mb-1">{step.thai}</p>
                                  <p className="text-slate-600">{step.french}</p>
                                  <p className="text-xs text-slate-500 mt-2 italic bg-white/50 p-2 rounded-lg">
                                    💡 {step.explanation}
                                  </p>
                                </div>
                                <button
                                  onClick={() => togglePhraseAudio(step.thai)}
                                  className={`p-2.5 rounded-full ${
                                    stepIdx === 0 
                                      ? "bg-green-200 text-green-700 hover:bg-green-300" 
                                      : "bg-purple-200 text-purple-700 hover:bg-purple-300"
                                  }`}
                                >
                                  <Volume2 className="w-5 h-5" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Expanded content */}
                        {selectedProgressivePhrase === phraseIdx && (
                          <div className="mt-4 space-y-4">
                            {/* Grammar pattern */}
                            {phrase.grammar_pattern && (
                              <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                                <h5 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                                  <BookOpen className="w-4 h-4" />
                                  Structure grammaticale
                                </h5>
                                <p className="font-mono text-amber-900 bg-white/50 p-2 rounded-lg">
                                  {phrase.grammar_pattern}
                                </p>
                              </div>
                            )}

                            {/* Substitution ideas */}
                            {phrase.substitution_ideas && phrase.substitution_ideas.length > 0 && (
                              <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
                                <h5 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
                                  <Edit3 className="w-4 h-4" />
                                  Idées de substitution
                                </h5>
                                <ul className="space-y-2">
                                  {phrase.substitution_ideas.map((idea, idx) => (
                                    <li key={idx} className="text-sm text-blue-700 flex items-start gap-2">
                                      <ChevronRight className="w-4 h-4 mt-0.5 text-blue-400" />
                                      {idea}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Practice exercise */}
                            {phrase.practice_exercise && (
                              <div className="bg-green-50 rounded-xl p-4 border border-green-200">
                                <h5 className="font-medium text-green-800 mb-3 flex items-center gap-2">
                                  <Mic className="w-4 h-4" />
                                  Exercice pratique
                                </h5>
                                <p className="text-green-800 font-medium mb-3">{phrase.practice_exercise.prompt}</p>
                                <div className="flex gap-2">
                                  <input
                                    type="text"
                                    value={exerciseAnswer}
                                    onChange={(e) => setExerciseAnswer(e.target.value)}
                                    placeholder="Ta réponse en thaï..."
                                    className="flex-1 px-4 py-2 border border-green-200 rounded-xl focus:outline-none focus:border-green-500 font-thai text-lg"
                                  />
                                  <Button
                                    onClick={() => {
                                      if (exerciseAnswer.trim() === phrase.practice_exercise.answer) {
                                        toast.success("Parfait ! 🎉");
                                        setExerciseResult({ correct: true });
                                      } else {
                                        setExerciseResult({ 
                                          correct: false, 
                                          answer: phrase.practice_exercise.answer
                                        });
                                      }
                                    }}
                                    className="bg-green-500 hover:bg-green-600 text-white"
                                  >
                                    <Check className="w-4 h-4" />
                                  </Button>
                                </div>
                                {exerciseResult && !exerciseResult.correct && (
                                  <div className="mt-3 p-3 bg-amber-100 rounded-xl text-amber-800">
                                    <p className="font-medium">Réponse: <span className="font-thai">{exerciseResult.answer}</span></p>
                                    <button
                                      onClick={() => togglePhraseAudio(exerciseResult.answer)}
                                      className="mt-2 flex items-center gap-1 text-sm bg-white px-3 py-1 rounded-lg"
                                    >
                                      <Volume2 className="w-4 h-4" />
                                      Écouter
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* STORY MODE */}
        {mode === "story" && (
          <>
        {/* Topic selection */}
        <div className="mt-4">
          <label className="text-sm font-medium text-slate-600 mb-2 block">
            Sujet de l'histoire (optionnel)
          </label>
          <input
            type="text"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="Ex: Au marché, Dans le BTS, Commander à manger..."
            className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:border-amber-500"
            data-testid="story-topic-input"
          />
          <div className="flex flex-wrap gap-2 mt-3">
            {(firstPerson ? firstPersonTopics : suggestedTopics).map((t, idx) => (
              <button
                key={idx}
                onClick={() => setTopic(t)}
                className={`px-3 py-1 rounded-full text-xs transition-all ${
                  topic === t
                    ? 'bg-teal-500 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-teal-100 hover:text-teal-700'
                }`}
                data-testid={`topic-suggestion-${idx}`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        {/* First Person Mode Toggle */}
        <div className="mt-4 bg-gradient-to-r from-violet-50 to-purple-50 rounded-2xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                firstPerson ? 'bg-violet-500' : 'bg-slate-200'
              }`}>
                <User className={`w-5 h-5 ${firstPerson ? 'text-white' : 'text-slate-500'}`} />
              </div>
              <div>
                <p className="font-medium text-slate-700">À la 1ère personne</p>
                <p className="text-xs text-slate-500">
                  {firstPerson 
                    ? "JE parle avec ครับ - Dialogues réalistes" 
                    : "Scène à la 3ème personne classique"}
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                setFirstPerson(!firstPerson);
                setTopic("");
              }}
              className={`relative w-14 h-8 rounded-full transition-all ${
                firstPerson ? 'bg-violet-500' : 'bg-slate-300'
              }`}
              data-testid="first-person-toggle"
            >
              <div className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow-md transition-all ${
                firstPerson ? 'left-7' : 'left-1'
              }`} />
            </button>
          </div>
          {firstPerson && (
            <div className="mt-3 pt-3 border-t border-violet-200">
              {/* PROMPT 3C: Sélecteur de persona Masculin/Féminin */}
              <p className="text-xs text-violet-700 font-medium mb-2">
                👤 Choisissez votre persona :
              </p>
              <div className="flex gap-2 mb-3">
                <button
                  onClick={() => setPersona("masculine")}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                    persona === "masculine"
                      ? 'bg-blue-500 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid="persona-masculine"
                >
                  👨 Masculin
                </button>
                <button
                  onClick={() => setPersona("feminine")}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                    persona === "feminine"
                      ? 'bg-pink-500 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid="persona-feminine"
                >
                  👩 Féminin
                </button>
              </div>
              <p className="text-xs text-violet-700 font-medium mb-2">
                ✨ Mode "Ma Scène" activé !
              </p>
              <ul className="text-xs text-violet-600 space-y-1">
                {persona === "masculine" ? (
                  <>
                    <li>• Vous parlez avec ผม: et ครับ</li>
                    <li>• Dialogues avec vendeurs, serveurs, chauffeurs...</li>
                    <li>• Pratiquez exactement ce que vous direz en Thaïlande</li>
                  </>
                ) : (
                  <>
                    <li>• Vous parlez avec ค่ะ (affirmatif) et คะ (question)</li>
                    <li>• Le sujet est omis naturellement</li>
                    <li>• Pratiquez exactement ce que vous direz en Thaïlande</li>
                  </>
                )}
              </ul>
            </div>
          )}
        </div>

        {/* Word count selector */}
        <div className="mt-4">
          <label className="text-sm font-medium text-slate-600 mb-3 block">
            Nombre de mots à utiliser
          </label>
          <div className="flex gap-2">
            {[10, 20, 30, 50, 100].map(count => (
              <button
                key={count}
                onClick={() => setWordCount(count)}
                className={`flex-1 py-3 rounded-xl font-medium transition-all ${
                  wordCount === count
                    ? 'bg-amber-500 text-white shadow-lg'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`word-count-${count}`}
              >
                {count}
              </button>
            ))}
          </div>
          <p className="text-xs text-amber-600 mt-2 text-center font-medium">
            {getStoryLengthHint(wordCount)}
          </p>
        </div>

        {/* Voice Speed Control */}
        <div className="mt-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Gauge className="w-5 h-5 text-orange-500" />
              <span className="font-medium text-slate-700">Vitesse de narration</span>
            </div>
            <Badge className="bg-orange-500 text-white">
              {getSpeedLabel(voiceSpeed)} ({voiceSpeed.toFixed(2)}x)
            </Badge>
          </div>
          <Slider
            value={[voiceSpeed]}
            onValueChange={(value) => setVoiceSpeed(value[0])}
            min={0.3}
            max={1.0}
            step={0.05}
            className="w-full"
            data-testid="story-voice-speed-slider"
          />
          <div className="flex justify-between text-xs text-slate-500 mt-1">
            <span>Très lent (0.3x)</span>
            <span>Modéré (0.7x)</span>
            <span>Normal (1x)</span>
          </div>
        </div>

        {/* Generate button */}
        <Button
          onClick={generateStory}
          disabled={isGenerating}
          className="w-full mt-4 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
          data-testid="generate-story-btn"
        >
          {isGenerating ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Génération en cours...
            </>
          ) : (
            <>
              <Wand2 className="w-5 h-5 mr-2" />
              Générer l'histoire
            </>
          )}
        </Button>

        {/* Story display */}
        {story && (
          <div className="mt-6 space-y-4">
            {/* Thai story */}
            <div className="bg-amber-50 rounded-2xl p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-amber-800">Scène en thaï</h4>
                <div className="flex gap-2">
                  <button
                    onClick={globalAudioState === 'playing' ? stopStory : (globalAudioState === 'audio_ready' ? resumePreloadedAudio : playStory)}
                    disabled={globalAudioState === 'loading' || globalAudioState === 'stopping'}
                    className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm transition-all ${
                      globalAudioState === 'loading'
                        ? 'bg-amber-400 text-white cursor-wait'
                        : globalAudioState === 'playing'
                          ? 'bg-red-500 text-white hover:bg-red-600'
                          : globalAudioState === 'stopping'
                            ? 'bg-slate-400 text-white cursor-wait'
                            : globalAudioState === 'audio_ready'
                              ? 'bg-green-500 text-white hover:bg-green-600'
                              : 'bg-amber-500 text-white hover:bg-amber-600'
                    }`}
                    data-testid="speak-story-thai"
                  >
                    {globalAudioState === 'loading' ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Préparation audio...
                      </>
                    ) : globalAudioState === 'playing' ? (
                      <>
                        <Pause className="w-4 h-4" />
                        Arrêter
                      </>
                    ) : globalAudioState === 'stopping' ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Arrêt...
                      </>
                    ) : globalAudioState === 'audio_ready' ? (
                      <>
                        <Play className="w-4 h-4" />
                        Audio prêt — Lire
                      </>
                    ) : (
                      <>
                        <Volume2 className="w-4 h-4" />
                        Écouter
                      </>
                    )}
                  </button>
                  <button
                    onClick={isRecording ? stopRecording : startRecording}
                    disabled={isTestingPronunciation || sentenceTestMode}
                    className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm transition-all ${
                      isRecording
                        ? 'bg-red-500 text-white animate-pulse'
                        : isTestingPronunciation
                        ? 'bg-slate-300 text-slate-500'
                        : sentenceTestMode
                        ? 'bg-slate-200 text-slate-400'
                        : 'bg-teal-500 text-white hover:bg-teal-600'
                    }`}
                    data-testid="test-pronunciation-btn"
                    title={sentenceTestMode ? "Désactive le mode phrase pour tester tout" : ""}
                  >
                    {isTestingPronunciation ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Analyse...
                      </>
                    ) : isRecording ? (
                      <>
                        <MicOff className="w-4 h-4" />
                        Arrêter
                      </>
                    ) : (
                      <>
                        <Mic className="w-4 h-4" />
                        Tester
                      </>
                    )}
                  </button>
                </div>
                
                {/* Toggle for sentence-by-sentence mode */}
                <div className="flex items-center justify-between mt-3 p-2 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200">
                  <div className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-purple-600" />
                    <span className="text-sm font-medium text-purple-800">Mode phrase par phrase</span>
                  </div>
                  <button
                    data-testid="phrase-mode-toggle"
                    onClick={() => {
                      setSentenceTestMode(!sentenceTestMode);
                      setSentenceResult(null);
                      setSelectedSentence(null);
                    }}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                      sentenceTestMode ? 'bg-purple-500' : 'bg-slate-300'
                    }`}
                  >
                    <span 
                      className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform shadow ${
                        sentenceTestMode ? 'translate-x-7' : 'translate-x-1'
                      }`} 
                    />
                  </button>
                </div>
              </div>

              {/* Story text display with rhythm markers */}
              <div className="font-thai text-xl sm:text-2xl text-slate-800 leading-relaxed whitespace-pre-line break-words">
                {story.story_thai.split(/(<<[^>]+>>)/).map((part, idx) => {
                  const match = part.match(/<<([^|]+)\|\|([^>]+)>>/);
                  if (match) {
                    const [, standard, street] = match;
                    return (
                      <span key={idx} className="inline-block relative group">
                        <span className="text-slate-800">{standard}</span>
                        <span className="ml-1 px-2 py-0.5 bg-purple-100 text-purple-700 rounded-lg text-sm font-medium border border-purple-300">
                          rue: {street}
                        </span>
                      </span>
                    );
                  }
                  // Format rhythm markers " / " with compact visual styling
                  return (
                    <span key={idx}>
                      {part.split(' / ').map((segment, segIdx, arr) => (
                        <span key={segIdx}>
                          {segment}
                          {segIdx < arr.length - 1 && (
                            <span className="text-purple-400 mx-0.5 select-none" title="Pause rythmique">·</span>
                          )}
                        </span>
                      ))}
                    </span>
                  );
                })}
              </div>
              <div className="mt-2 text-xs text-slate-500 flex items-center gap-1">
                <span className="text-purple-400">·</span>
                <span>= pause rythmique</span>
                {story.story_thai.includes('<<') && (
                  <>
                    <span className="ml-3 px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">rue</span>
                    <span>= variante familière</span>
                  </>
                )}
              </div>
              
              {/* Sentence-by-sentence test mode */}
              {sentenceTestMode && (
                <div className="mt-4 p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl border-2 border-purple-200">
                  <h4 className="font-bold text-purple-800 mb-3 flex items-center gap-2">
                    <Target className="w-5 h-5" />
                    Test phrase par phrase
                  </h4>
                  <p className="text-xs text-purple-600 mb-3">
                    Clique sur 🎤 pour tester une phrase, ou 🔊 pour l'écouter d'abord
                  </p>
                  
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {parseStoryIntoSentences(story.story_thai).map((sentence, idx) => (
                      <div 
                        key={sentence.id}
                        className={`p-3 rounded-xl border-2 transition-all ${
                          sentenceResult?.sentenceId === sentence.id
                            ? sentenceResult.score >= 85
                              ? 'bg-green-50 border-green-300'
                              : sentenceResult.score >= 70
                              ? 'bg-yellow-50 border-yellow-300'
                              : 'bg-red-50 border-red-300'
                            : testingSentenceIndex === idx
                            ? 'bg-purple-100 border-purple-400'
                            : 'bg-white border-slate-200 hover:border-purple-300'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            {sentence.speaker && (
                              <span className="text-xs font-bold text-purple-600 mr-2">
                                {sentence.speaker}:
                              </span>
                            )}
                            <span className="font-thai text-xl text-slate-800">{sentence.text}</span>
                          </div>
                          
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                              onClick={() => playSentence(sentence.text)}
                              className="p-2 rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors"
                              title="Écouter"
                            >
                              <Volume2 className="w-4 h-4" />
                            </button>
                            
                            {testingSentenceIndex === idx && isRecording ? (
                              <button
                                onClick={stopSentenceRecording}
                                className="p-2 rounded-full bg-red-500 text-white animate-pulse"
                                title="Arrêter"
                              >
                                <MicOff className="w-4 h-4" />
                              </button>
                            ) : (
                              <button
                                onClick={() => startSentenceRecording(sentence, idx)}
                                disabled={isTestingPronunciation || isRecording}
                                className={`p-2 rounded-full transition-colors ${
                                  isTestingPronunciation || isRecording
                                    ? 'bg-slate-100 text-slate-400'
                                    : 'bg-purple-100 text-purple-600 hover:bg-purple-200'
                                }`}
                                title="Tester ma voix"
                              >
                                {isTestingPronunciation && testingSentenceIndex === idx ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Mic className="w-4 h-4" />
                                )}
                              </button>
                            )}
                            
                            {sentenceResult?.sentenceId === sentence.id && (
                              <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                                sentenceResult.score >= 90 ? 'bg-green-500 text-white' :
                                sentenceResult.score >= 70 ? 'bg-lime-500 text-white' :
                                sentenceResult.score >= 50 ? 'bg-yellow-500 text-white' :
                                sentenceResult.score >= 30 ? 'bg-orange-500 text-white' :
                                'bg-red-500 text-white'
                              }`}>
                                {Math.round(sentenceResult.score / 10)}/10
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {sentenceResult?.sentenceId === sentence.id && (
                          <div className="mt-3 pt-3 border-t border-slate-200">
                            <div className="text-xs space-y-2">
                              <div>
                                <span className="font-bold text-slate-600">Transcrit: </span>
                                <span className="font-thai text-slate-700">{sentenceResult.transcription}</span>
                              </div>
                              <div className="flex flex-wrap gap-1.5">
                                {sentenceResult.word_matches?.slice(0, 15).map((match, i) => {
                                  // Get STRICT score on 1-10 scale
                                  const score = match.score ?? (match.correct ? 10 : 1);
                                  const errors = match.errors || [];
                                  const tip = match.tip || '';
                                  const phoneticExpected = match.phonetic_expected || '';
                                  const phoneticHeard = match.phonetic_heard || '';
                                  
                                  // Color based on score level (1-10 scale)
                                  let colorClass = 'bg-red-100 text-red-700 border-red-300';
                                  let bgGradient = 'from-red-50 to-red-100';
                                  if (score === 10) {
                                    colorClass = 'bg-green-100 text-green-700 border-green-300';
                                    bgGradient = 'from-green-50 to-green-100';
                                  } else if (score >= 8) {
                                    colorClass = 'bg-lime-100 text-lime-700 border-lime-300';
                                    bgGradient = 'from-lime-50 to-lime-100';
                                  } else if (score >= 6) {
                                    colorClass = 'bg-yellow-100 text-yellow-700 border-yellow-300';
                                    bgGradient = 'from-yellow-50 to-yellow-100';
                                  } else if (score >= 4) {
                                    colorClass = 'bg-orange-100 text-orange-700 border-orange-300';
                                    bgGradient = 'from-orange-50 to-orange-100';
                                  }
                                  
                                  // Build detailed tooltip
                                  let tooltipContent = `Note: ${score}/10`;
                                  if (phoneticExpected) tooltipContent += `\n📢 Attendu: ${phoneticExpected}`;
                                  if (phoneticHeard) tooltipContent += `\n👂 Entendu: ${phoneticHeard}`;
                                  if (errors.length > 0) tooltipContent += `\n❌ Erreurs: ${errors.join(', ')}`;
                                  if (tip) tooltipContent += `\n💡 ${tip}`;
                                  
                                  return (
                                    <div
                                      key={i}
                                      className={`group relative px-2 py-1 rounded-lg text-xs font-thai border bg-gradient-to-br ${bgGradient} ${colorClass} cursor-help transition-all hover:scale-105 hover:shadow-md`}
                                      title={tooltipContent}
                                    >
                                      <div className="flex items-center gap-1">
                                        <span className="font-medium">{match.expected}</span>
                                        <span className={`font-bold text-[10px] px-1 py-0.5 rounded ${
                                          score === 10 ? 'bg-green-200 text-green-800' : 
                                          score >= 7 ? 'bg-lime-200 text-lime-800' : 
                                          score >= 5 ? 'bg-yellow-200 text-yellow-800' : 
                                          'bg-red-200 text-red-800'
                                        }`}>
                                          {score}/10
                                        </span>
                                      </div>
                                      {/* Show transcribed word if different */}
                                      {match.transcribed && match.transcribed !== match.expected && (
                                        <div className="text-[9px] opacity-75 mt-0.5">
                                          → {match.transcribed}
                                        </div>
                                      )}
                                      {/* Show error indicators */}
                                      {errors.length > 0 && (
                                        <div className="flex gap-0.5 mt-0.5">
                                          {errors.slice(0, 2).map((err, ei) => (
                                            <span key={ei} className="text-[8px] bg-white/50 px-1 rounded">
                                              {err.includes('ton') ? '🎵' : err.includes('consonne') ? '🔤' : err.includes('voyelle') ? '🔊' : '⚠️'}
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                              {sentenceResult.detailed_corrections && (
                                <div className="p-3 bg-white rounded-lg text-xs text-slate-700 whitespace-pre-line max-h-64 overflow-y-auto border border-slate-200 shadow-inner leading-relaxed">
                                  {sentenceResult.detailed_corrections}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* French translation - PHASE 1: Hidden by default, toggle button to reveal */}
            <div className="bg-blue-50 rounded-2xl p-4 border border-blue-200">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-blue-800 flex items-center gap-2">
                  <span className="text-lg">🇫🇷</span>
                  Traduction française
                </h4>
                <button
                  onClick={() => setShowTranslation(!showTranslation)}
                  className="px-3 py-1 text-sm rounded-full bg-blue-100 hover:bg-blue-200 text-blue-700 transition-colors"
                  data-testid="toggle-translation-btn"
                >
                  {showTranslation ? 'Masquer' : 'Voir la traduction'}
                </button>
              </div>
              {showTranslation ? (
                <p className="text-lg text-blue-900 leading-relaxed whitespace-pre-line">
                  {story.story_french_natural || story.story_french}
                </p>
              ) : (
                <p className="text-sm text-blue-600 italic">
                  Essaie d'abord de comprendre le thaï, puis clique pour vérifier ta compréhension.
                </p>
              )}
            </div>

            {/* Mots clés de l'histoire - MASQUÉ POUR BÊTA
                Raison: Impossible de garantir à 100% que tous les mots affichés 
                sont présents dans l'histoire courante. Sera réactivé après bêta
                avec extraction stricte depuis le texte de l'histoire.
            */}
            {/* 
            <div className="bg-white rounded-2xl p-4 border border-slate-200">
              <h4 className="font-medium text-slate-700 mb-3">
                Mots clés de l'histoire
              </h4>
              <div className="flex flex-wrap gap-2">
                {story.words_used.slice(0, 7).map((word, idx) => (
                  <button
                    key={idx}
                    onClick={() => speakWithSpeed(word.thai)}
                    className="px-3 py-2 bg-slate-100 hover:bg-amber-100 rounded-full text-base transition-colors group"
                  >
                    <span className="font-thai text-lg">{word.thai}</span>
                    <span className="text-slate-400 ml-1">({word.french})</span>
                    <Volume2 className="w-3 h-3 inline ml-1 opacity-0 group-hover:opacity-100 text-amber-500" />
                  </button>
                ))}
              </div>
            </div>
            */}

            {/* Culture et usage - PROMPT 3E: Validation ancrée stricte */}
            {validateAnchoredBlock(story.street_notes, story.story_thai, 'street_notes_culture') && (
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-4 border border-purple-200">
                <h4 className="font-medium text-purple-800 mb-2 flex items-center gap-2">
                  <span className="text-lg">🌏</span>
                  Culture et usage
                </h4>
                <p className="text-purple-700 text-sm leading-relaxed whitespace-pre-line">
                  {validateAnchoredBlock(story.street_notes, story.story_thai, 'street_notes_culture')}
                </p>
              </div>
            )}

            {/* BOUTON SECONDAIRE DÉSACTIVÉ POUR LA BÊTA
                L'idée d'un mode lecture en série sera implémentée après bêta.
                Pour l'instant, utiliser le bouton principal "Générer l'histoire".
            */}

            {/* PHASE 1: 5 KEY WORDS TO WRITE - MASQUÉ SOUS LA SCÈNE
                Raison: Ce bloc crée un doublon avec le bloc vert dans le Support d'apprentissage.
                Les mots clés sont désormais visibles UNIQUEMENT dans le Support d'apprentissage.
                Les données words_to_write sont conservées pour le bloc vert.
            */}
            {/* 
            {story.words_to_write && story.words_to_write.length > 0 && (
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-4 border-2 border-amber-300 animate-pulse-once">
                <h4 className="font-medium text-amber-800 mb-3 flex items-center gap-2">
                  <span className="text-xl">✍️</span>
                  5 mots clés à écrire — 5x chacun
                  <span className="ml-auto bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full text-xs font-bold">
                    {completedWords.filter(w => story.words_to_write.some(sw => sw.thai === w)).length} / 5 maîtrisés
                  </span>
                </h4>
                <p className="text-amber-700 text-sm mb-3">
                  Expressions clés de l'histoire — pratique l'écriture pour les mémoriser !
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {story.words_to_write.map((word, idx) => {
                    const isCompleted = completedWords.includes(word.thai);
                    return (
                      <button
                        key={`new-${idx}`}
                        onClick={() => !isCompleted && startPracticeWord(word)}
                        disabled={isCompleted}
                        className={`p-3 rounded-xl text-left transition-all flex items-center gap-3 ${
                          isCompleted
                            ? 'bg-green-100 border-2 border-green-300'
                            : 'bg-white border-2 border-amber-300 hover:border-amber-500 hover:bg-amber-50 shadow-sm'
                        }`}
                        data-testid={`new-word-${idx}`}
                      >
                        <div className="flex-1">
                          <p className="font-thai text-xl text-amber-900">{word.thai}</p>
                          <p className="text-xs text-amber-700">{word.french}</p>
                          <p className="text-xs text-amber-500 italic">{word.category}</p>
                        </div>
                        {isCompleted ? (
                          <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-amber-200 flex items-center justify-center">
                            <span className="text-amber-700 font-bold">✍️</span>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            */}

            {/* Pronunciation Test Results - STRICT MODE */}
            {pronunciationResult && (
              <div className={`rounded-2xl p-5 border-2 ${
                pronunciationResult.score >= 80 
                  ? 'bg-green-50 border-green-300' 
                  : pronunciationResult.score >= 60
                  ? 'bg-yellow-50 border-yellow-300'
                  : pronunciationResult.score >= 40
                  ? 'bg-orange-50 border-orange-300'
                  : 'bg-red-50 border-red-300'
              }`}>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4 className="font-bold text-lg flex items-center gap-2">
                      <Target className="w-5 h-5" />
                      Évaluation stricte
                    </h4>
                    <p className="text-xs text-slate-600">Notation sur 10 points</p>
                  </div>
                  <div className={`text-5xl font-black ${
                    pronunciationResult.score >= 80 ? 'text-green-600' :
                    pronunciationResult.score >= 60 ? 'text-yellow-600' :
                    pronunciationResult.score >= 40 ? 'text-orange-600' :
                    'text-red-600'
                  }`}>
                    {Math.round(pronunciationResult.score / 10)}<span className="text-2xl">/10</span>
                  </div>
                </div>
                
                <p className={`text-sm mb-4 font-bold ${
                  pronunciationResult.score >= 80 ? 'text-green-700' :
                  pronunciationResult.score >= 60 ? 'text-yellow-700' :
                  pronunciationResult.score >= 40 ? 'text-orange-700' :
                  'text-red-700'
                }`}>{pronunciationResult.feedback}</p>
                
                <div className="bg-white rounded-xl p-4 mb-4 border border-slate-200">
                  <div className="grid grid-cols-1 gap-3 mb-3">
                    <div>
                      <p className="text-xs font-bold text-green-600 mb-1">🎯 ATTENDU:</p>
                      <p className="text-sm font-thai text-slate-800 bg-green-50 p-2 rounded">{pronunciationResult.expected?.slice(0, 150) || ''}...</p>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-blue-600 mb-1">🎤 TRANSCRIT:</p>
                      <p className="text-sm font-thai text-slate-800 bg-blue-50 p-2 rounded">{pronunciationResult.transcription?.slice(0, 150) || ''}...</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white/50 rounded-xl p-3 mb-4">
                  <p className="text-xs font-bold text-slate-600 mb-2">📊 ANALYSE MOT PAR MOT:</p>
                  <div className="flex flex-wrap gap-1">
                    {pronunciationResult.word_matches.slice(0, 25).map((match, idx) => {
                      // Get STRICT score on 1-10 scale (NOT percentage)
                      const score = match.score ?? (match.correct ? 10 : 1);
                      
                      // Color based on score level (1-10 scale)
                      let colorClass = 'bg-red-100 text-red-700 border-red-300';
                      let icon = '❌';
                      if (score === 10) {
                        colorClass = 'bg-green-100 text-green-700 border-green-300';
                        icon = '✅';
                      } else if (score >= 8) {
                        colorClass = 'bg-lime-100 text-lime-700 border-lime-300';
                        icon = '👍';
                      } else if (score >= 6) {
                        colorClass = 'bg-yellow-100 text-yellow-700 border-yellow-300';
                        icon = '⚠️';
                      } else if (score >= 4) {
                        colorClass = 'bg-orange-100 text-orange-700 border-orange-300';
                        icon = '🔶';
                      }
                      
                      return (
                        <span
                          key={idx}
                          className={`px-2 py-1 rounded text-sm font-thai border ${colorClass}`}
                          title={`Note: ${score}/10 - Entendu: ${match.transcribed || '?'}`}
                        >
                          {match.expected}
                          <span className="text-xs ml-1 font-sans font-bold">
                            {score}/10
                          </span>
                        </span>
                      );
                    })}
                    {pronunciationResult.word_matches.length > 25 && (
                      <span className="text-xs text-slate-500 self-center">
                        +{pronunciationResult.word_matches.length - 25}
                      </span>
                    )}
                  </div>
                  <div className="mt-2 text-xs font-medium flex flex-wrap gap-2">
                    <span className="text-green-600">✅ {pronunciationResult.word_matches.filter(m => (m.score ?? (m.correct ? 10 : 1)) === 10).length} parfaits (10/10)</span>
                    <span className="text-lime-600">👍 {pronunciationResult.word_matches.filter(m => { const s = m.score ?? (m.correct ? 10 : 1); return s >= 8 && s < 10; }).length} bons (8-9/10)</span>
                    <span className="text-yellow-600">⚠️ {pronunciationResult.word_matches.filter(m => { const s = m.score ?? (m.correct ? 10 : 1); return s >= 6 && s < 8; }).length} moyens (6-7/10)</span>
                    <span className="text-red-600">❌ {pronunciationResult.word_matches.filter(m => (m.score ?? (m.correct ? 10 : 1)) < 6).length} à revoir (&lt;6/10)</span>
                  </div>
                </div>

                {pronunciationResult.detailed_corrections && (
                  <div className="bg-white rounded-xl p-4 border-2 border-purple-300 shadow-lg">
                    <h5 className="font-bold text-purple-800 mb-3 flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-purple-500" />
                      📚 Analyse pédagogique détaillée
                    </h5>
                    <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-line max-h-96 overflow-y-auto bg-purple-50/50 p-3 rounded-lg">
                      {pronunciationResult.detailed_corrections}
                    </div>
                  </div>
                )}
                
                <div className="flex gap-2 mt-4">
                  <button
                    onClick={() => {
                      setPronunciationResult(null);
                      startRecording();
                    }}
                    className="flex-1 px-4 py-3 bg-gradient-to-r from-orange-500 to-red-500 text-white rounded-full text-sm font-bold hover:from-orange-600 hover:to-red-600 transition-colors flex items-center justify-center gap-2"
                  >
                    <Mic className="w-5 h-5" />
                    Réessayer
                  </button>
                  <button
                    onClick={() => setPronunciationResult(null)}
                    className="px-4 py-3 text-sm text-slate-500 hover:text-slate-700 border border-slate-300 rounded-full"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            )}

            {/* PHASE 1: Quiz Button FIRST - User should test before seeing help */}
            <Button
              onClick={startQuiz}
              disabled={isLoadingQuiz}
              className="w-full rounded-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
              data-testid="quiz-btn"
            >
              {isLoadingQuiz ? (
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <HelpCircle className="w-5 h-5 mr-2" />
              )}
              Tester mes connaissances
            </Button>

            {/* Learning Support Button - SECOND, after Quiz */}
            <Button
              onClick={openLearningSupport}
              className="w-full rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-600 hover:to-emerald-600"
              data-testid="learning-support-btn"
            >
              <GraduationCap className="w-5 h-5 mr-2" />
              Support d'apprentissage
            </Button>

            {/* Quiz Panel */}
            {showQuiz && (
              <div className="space-y-4 border-t-2 border-amber-200 pt-4 mt-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-amber-700">
                    <HelpCircle className="w-6 h-6" />
                    <h3 className="font-heading text-xl">Quiz sur l'histoire</h3>
                  </div>
                  <button
                    onClick={() => setShowQuiz(false)}
                    className="text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {isLoadingQuiz ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <Loader2 className="w-10 h-10 animate-spin text-amber-500 mb-4" />
                    <p className="text-amber-700">Manee prépare ton quiz...</p>
                  </div>
                ) : quizResult ? (
                  <div className="space-y-4">
                    <div className={`rounded-2xl p-6 text-center ${
                      quizResult.percentage >= 80 ? 'bg-green-50 border border-green-200' :
                      quizResult.percentage >= 60 ? 'bg-blue-50 border border-blue-200' :
                      quizResult.percentage >= 40 ? 'bg-amber-50 border border-amber-200' :
                      'bg-slate-50 border border-slate-200'
                    }`}>
                      <div className="text-5xl mb-2">
                        {quizResult.percentage >= 80 ? '🎉' :
                         quizResult.percentage >= 60 ? '👍' :
                         quizResult.percentage >= 40 ? '💪' : '🌱'}
                      </div>
                      <p className="text-2xl font-bold text-slate-800">
                        {quizResult.score} / {quizResult.total}
                      </p>
                      <p className="text-sm text-slate-600 mt-1">
                        {Math.round(quizResult.percentage)}% de bonnes réponses
                      </p>
                      <p className="text-sm mt-3 text-slate-700">
                        {quizResult.feedback}
                      </p>
                    </div>

                    <div className="space-y-3">
                      {quizResult.details.map((detail, idx) => {
                        // Get the appropriate explanation based on correctness
                        const getExplanation = () => {
                          if (detail.is_correct) {
                            // Show correct explanation
                            return detail.correct_explanation || detail.explanation || '';
                          } else {
                            // Show distractor explanation if available
                            const distractorExplanations = detail.distractor_explanations || [];
                            const distractorIdx = detail.user_distractor_index;
                            if (distractorIdx !== null && distractorIdx !== undefined && distractorExplanations[distractorIdx]) {
                              return distractorExplanations[distractorIdx];
                            }
                            // Fallback to legacy explanation
                            return detail.explanation || detail.correct_explanation || '';
                          }
                        };
                        
                        return (
                          <div key={idx} className={`rounded-xl p-4 border ${
                            detail.is_correct 
                              ? 'bg-green-50 border-green-200' 
                              : 'bg-red-50 border-red-200'
                          }`}>
                            <div className="flex items-start gap-2">
                              {detail.is_correct ? (
                                <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                              ) : (
                                <X className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                              )}
                              <div className="flex-1">
                                <p className="font-medium text-sm text-slate-800">{detail.question}</p>
                                {!detail.is_correct && (
                                  <p className="text-xs text-red-600 mt-1">
                                    Ta réponse: {detail.user_answer}
                                  </p>
                                )}
                                <p className="text-xs text-green-700 mt-1">
                                  ✓ {detail.correct_answer}
                                </p>
                                <p className="text-xs text-slate-600 mt-2 italic">
                                  {getExplanation()}
                                </p>
                                {/* Show correct explanation too if user was wrong (learning opportunity) */}
                                {!detail.is_correct && detail.correct_explanation && detail.correct_explanation !== getExplanation() && (
                                  <p className="text-xs text-green-700 mt-2 italic border-t border-green-200 pt-2">
                                    💡 {detail.correct_explanation}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <Button
                      onClick={() => {
                        setQuizResult(null);
                        setShowQuiz(false);
                      }}
                      className="w-full rounded-full"
                    >
                      Fermer le quiz
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      {quizQuestions.map((_, idx) => (
                        <div
                          key={idx}
                          className={`flex-1 h-2 rounded-full transition-all ${
                            quizAnswers[idx] !== -1 
                              ? 'bg-amber-500' 
                              : 'bg-slate-200'
                          }`}
                        />
                      ))}
                    </div>

                    {quizQuestions.map((q, qIdx) => (
                      <div key={qIdx} className="bg-white rounded-xl p-4 border border-amber-200">
                        <div className="flex items-start gap-2 mb-3">
                          <Badge className={`text-xs ${
                            q.type === 'comprehension' ? 'bg-blue-100 text-blue-700' :
                            q.type === 'vocabulary' ? 'bg-purple-100 text-purple-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {q.type === 'comprehension' ? 'Compréhension' :
                             q.type === 'vocabulary' ? 'Vocabulaire' : 'Grammaire'}
                          </Badge>
                        </div>
                        <p className="font-medium text-slate-800 mb-3">{q.question}</p>
                        <div className="space-y-2">
                          {q.options.map((option, oIdx) => (
                            <button
                              key={oIdx}
                              onClick={() => selectAnswer(qIdx, oIdx)}
                              className={`w-full text-left p-3 rounded-lg border transition-all ${
                                quizAnswers[qIdx] === oIdx
                                  ? 'bg-amber-100 border-amber-400 text-amber-800'
                                  : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
                              }`}
                            >
                              <span className="font-medium mr-2">
                                {String.fromCharCode(65 + oIdx)}.
                              </span>
                              {option}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}

                    <Button
                      onClick={submitQuiz}
                      disabled={quizAnswers.includes(-1)}
                      className="w-full rounded-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                    >
                      <Check className="w-5 h-5 mr-2" />
                      Valider mes réponses
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Learning Support Panel */}
            {showLearningSupport && (
              <div className="space-y-4 border-t-2 border-teal-200 pt-4 mt-4">
                <div className="flex items-center gap-2 text-teal-700">
                  <GraduationCap className="w-6 h-6" />
                  <h3 className="font-heading text-xl">Support d'apprentissage</h3>
                </div>

                {/* Grammar Notes Section - Manee t'explique */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-4 border border-blue-200">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-medium text-blue-800 flex items-center gap-2">
                        <BookOpen className="w-5 h-5" />
                        Manee t'explique
                      </h4>
                      <p className="text-xs text-blue-500 mt-0.5">Explications simples, naturelles et vérifiées</p>
                    </div>
                  </div>
                  
                  {isLoadingGrammar ? (
                    <div className="flex items-center justify-center py-6">
                      <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                      <span className="ml-2 text-blue-600">Manee prépare ses conseils...</span>
                    </div>
                  ) : grammarNotesStructured ? (
                    /* PROMPT 3F: Structure stable du Support d'apprentissage */
                    <div className="space-y-4" data-testid="ploy-structured-notes">
                      
                      {/* Section: Tons vérifiés (toujours affichée si disponible) */}
                      {grammarNotesStructured.tones && grammarNotesStructured.tones.length > 0 && (
                        <div className="bg-white rounded-xl p-4 border border-blue-100">
                          <h5 className="font-medium text-purple-700 mb-3 flex items-center gap-2">
                            <Volume2 className="w-4 h-4" />
                            Lecture / tons (données vérifiées)
                          </h5>
                          <div className="space-y-2">
                            {grammarNotesStructured.tones.map((tone, idx) => (
                              <div key={idx} className="flex items-start gap-3 p-2 bg-purple-50 rounded-lg">
                                <span className="font-thai text-xl text-purple-800">{tone.thai}</span>
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                      tone.tone === 'haut' ? 'bg-red-100 text-red-700' :
                                      tone.tone === 'bas' ? 'bg-blue-100 text-blue-700' :
                                      tone.tone === 'montant' ? 'bg-green-100 text-green-700' :
                                      tone.tone === 'descendant' ? 'bg-orange-100 text-orange-700' :
                                      'bg-gray-100 text-gray-700'
                                    }`}>
                                      ton {tone.tone}
                                    </span>
                                    {tone.verified && (
                                      <CheckCircle2 className="w-3 h-3 text-green-500" />
                                    )}
                                  </div>
                                  <p className="text-sm text-slate-600 mt-1">{tone.meaning_fr}</p>
                                </div>
                                <button
                                  onClick={() => speakWithSpeed(tone.thai)}
                                  className="p-1.5 rounded-full bg-purple-100 text-purple-600 hover:bg-purple-200"
                                >
                                  <Volume2 className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Support d'apprentissage = 4 blocs + vocabulaire */}
                      {buildDetailedBlocks(grammarNotesStructured, story?.story_thai).map((block, idx) => (
                        <div key={idx} className="bg-white rounded-xl p-4 border border-blue-100">
                          <h5 className={`font-medium mb-2 flex items-center gap-2 ${
                            block.type === 'phrase_utile' ? 'text-blue-700' :
                            block.type === 'grammaire' ? 'text-indigo-700' :
                            block.type === 'usage_naturel' ? 'text-teal-700' :
                            'text-amber-700'
                          }`}>
                            {block.type === 'phrase_utile' ? <MessageCircle className="w-4 h-4" /> :
                             block.type === 'grammaire' ? <BookOpen className="w-4 h-4" /> :
                             block.type === 'usage_naturel' ? <Users className="w-4 h-4" /> :
                             <Lightbulb className="w-4 h-4" />}
                            {block.title}
                            {block.is_fallback && (
                              <span className="text-xs text-slate-400 ml-2">(auto)</span>
                            )}
                          </h5>
                          <p className="text-sm text-slate-700">{block.explanation_fr}</p>
                        </div>
                      ))}

                      {/* Vocabulaire important */}
                      {(() => {
                        const vocab = buildVocabularyBlock(grammarNotesStructured, story?.story_thai);
                        return vocab.length > 0 && (
                          <div className="bg-white rounded-xl p-4 border border-indigo-200">
                            <h5 className="font-medium text-indigo-700 mb-3 flex items-center gap-2">
                              <BookText className="w-4 h-4" />
                              Vocabulaire important
                            </h5>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {vocab.map((v, idx) => (
                                <div key={idx} className="bg-indigo-50 rounded-lg p-2 flex items-center gap-2">
                                  <span className="font-thai text-indigo-800">{v.thai}</span>
                                  <span className="text-slate-400">=</span>
                                  <span className="text-sm text-slate-600">{v.french}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Section: Erreur à éviter (si pertinent) */}
                      {grammarNotesStructured.confusions && grammarNotesStructured.confusions.length > 0 && (
                        <div className="bg-white rounded-xl p-4 border border-amber-200">
                          <h5 className="font-medium text-amber-700 mb-2 flex items-center gap-2">
                            <Target className="w-4 h-4" />
                            Erreur à éviter
                          </h5>
                          <div className="space-y-3">
                            {grammarNotesStructured.confusions.map((conf, idx) => (
                              <div key={idx} className="bg-amber-50 rounded-lg p-3">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="font-thai text-lg text-amber-800">{conf.wordA}</span>
                                  <span className="text-xs text-slate-500">({conf.meaningA_fr})</span>
                                  <span className="text-amber-500 font-bold">≠</span>
                                  <span className="font-thai text-lg text-amber-800">{conf.wordB}</span>
                                  <span className="text-xs text-slate-500">({conf.meaningB_fr})</span>
                                </div>
                                <p className="text-sm text-amber-700">{conf.reason_fr}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : grammarNotes ? (
                    /* Fallback: Plain text rendering (backward compatibility) */
                    <div className="text-slate-700 text-sm leading-relaxed whitespace-pre-line">
                      {grammarNotes}
                    </div>
                  ) : (
                    <p className="text-blue-600 text-sm">Chargement des notes...</p>
                  )}
                </div>

                {/* PROMPT 3C: Practice Writing Section - SECTION REPLIÉE PAR DÉFAUT */}
                <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl border border-emerald-200 overflow-hidden">
                  {/* Header clickable pour déplier/replier */}
                  <button
                    onClick={() => setShowWritingPractice(!showWritingPractice)}
                    className="w-full p-4 flex items-center justify-between hover:bg-emerald-100/50 transition-colors"
                    data-testid="writing-practice-toggle"
                  >
                    <div className="flex items-center gap-2">
                      <Edit3 className="w-5 h-5 text-emerald-600" />
                      <h4 className="font-medium text-emerald-800">
                        Pratiquer l'écriture (5 mots clés)
                      </h4>
                    </div>
                    <div className="flex items-center gap-2">
                      {completedWords.length > 0 && (
                        <span className="text-xs bg-emerald-200 text-emerald-700 px-2 py-1 rounded-full">
                          {completedWords.length}/5 ✓
                        </span>
                      )}
                      {showWritingPractice ? (
                        <ChevronUp className="w-5 h-5 text-emerald-600" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-emerald-600" />
                      )}
                    </div>
                  </button>
                  
                  {/* Contenu dépliable */}
                  {showWritingPractice && (
                    <div className="p-4 pt-0 border-t border-emerald-200">
                      <p className="text-xs text-emerald-600 mb-3">
                        Entraîne-toi à écrire chaque mot 5 fois pour le mémoriser.
                      </p>

                  <div className="mb-4">
                    {(() => {
                      // RÈGLE ABSOLUE: TOUJOURS 5 mots clés (compléter si nécessaire)
                      const FIXED_PRACTICE_COUNT = 5;
                      
                      // Calculer combien de mots sont réellement disponibles
                      let availableWords = 0;
                      const existingThais = new Set();
                      
                      if (story.words_to_write) {
                        story.words_to_write.forEach(w => {
                          if (!existingThais.has(w.thai)) {
                            existingThais.add(w.thai);
                            availableWords++;
                          }
                        });
                      }
                      
                      if (story.words_used && availableWords < FIXED_PRACTICE_COUNT) {
                        story.words_used.forEach(w => {
                          if (!existingThais.has(w.thai) && availableWords < FIXED_PRACTICE_COUNT) {
                            existingThais.add(w.thai);
                            availableWords++;
                          }
                        });
                      }
                      
                      // Toujours afficher 5 (ou le max disponible si moins)
                      const totalForPractice = Math.min(availableWords, FIXED_PRACTICE_COUNT);
                      const completed = Math.min(completedWords.length, totalForPractice);
                      
                      return (
                        <>
                          <div className="flex items-center justify-between text-sm text-emerald-700 mb-2">
                            <span>Progression</span>
                            <span>{completed} / {totalForPractice} mots clés maîtrisés</span>
                          </div>
                          <div className="w-full bg-emerald-100 rounded-full h-2">
                            <div 
                              className="bg-emerald-500 h-2 rounded-full transition-all"
                              style={{ width: `${totalForPractice > 0 ? (completed / totalForPractice) * 100 : 0}%` }}
                            />
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  {practiceWord ? (
                    <div className="bg-white rounded-xl p-4 border border-emerald-200">
                      <div className="text-center mb-4">
                        <p className="text-sm text-slate-500 mb-1">Écris ce mot :</p>
                        <p className="font-thai text-4xl text-emerald-700">{practiceWord.thai}</p>
                        <p className="text-slate-500 mt-1">({practiceWord.french})</p>
                        <button
                          onClick={() => speakWithSpeed(practiceWord.thai)}
                          className="mt-2 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm hover:bg-emerald-200 transition-colors"
                        >
                          <Volume2 className="w-4 h-4 inline mr-1" />
                          Écouter
                        </button>
                      </div>

                      <div className="flex justify-center gap-2 mb-4">
                        {[0, 1, 2, 3, 4].map((i) => (
                          <div
                            key={i}
                            className={`w-4 h-4 rounded-full transition-all ${
                              i < practiceCount
                                ? 'bg-emerald-500'
                                : 'bg-slate-200'
                            }`}
                          />
                        ))}
                      </div>

                      <form onSubmit={handlePracticeSubmit} className="space-y-3">
                        <input
                          ref={practiceInputRef}
                          type="text"
                          value={practiceInput}
                          onChange={(e) => setPracticeInput(e.target.value)}
                          placeholder="Écris le mot ici..."
                          className="w-full px-4 py-3 text-center font-thai text-2xl border-2 border-emerald-200 rounded-xl focus:outline-none focus:border-emerald-500"
                          autoComplete="off"
                          data-testid="practice-input"
                        />
                        <div className="flex gap-2">
                          <Button
                            type="submit"
                            className="flex-1 rounded-full bg-emerald-500 hover:bg-emerald-600"
                          >
                            <Check className="w-4 h-4 mr-2" />
                            Valider ({practiceCount}/5)
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setPracticeWord(null)}
                            className="rounded-full"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </form>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {/* RÈGLE ABSOLUE: Exactement 5 mots clés à écrire pour TOUTES les histoires */}
                      {(() => {
                        // TOUJOURS exactement 5 éléments - compléter si nécessaire
                        const FIXED_PRACTICE_COUNT = 5;
                        
                        // Commencer avec words_to_write si disponible
                        let wordsForPractice = [];
                        
                        if (story.words_to_write && story.words_to_write.length > 0) {
                          wordsForPractice = [...story.words_to_write];
                        }
                        
                        // Si pas assez de mots, compléter avec words_used
                        if (wordsForPractice.length < FIXED_PRACTICE_COUNT && story.words_used) {
                          const existingThais = new Set(wordsForPractice.map(w => w.thai));
                          for (const word of story.words_used) {
                            if (!existingThais.has(word.thai) && wordsForPractice.length < FIXED_PRACTICE_COUNT) {
                              wordsForPractice.push(word);
                              existingThais.add(word.thai);
                            }
                          }
                        }
                        
                        // Limiter à exactement 5
                        wordsForPractice = wordsForPractice.slice(0, FIXED_PRACTICE_COUNT);
                        
                        return wordsForPractice.map((word, idx) => {
                          const isCompleted = completedWords.includes(word.thai);
                          return (
                            <button
                              key={idx}
                              onClick={() => !isCompleted && startPracticeWord(word)}
                              disabled={isCompleted}
                              className={`p-3 rounded-xl text-left transition-all ${
                                isCompleted
                                  ? 'bg-emerald-100 border-2 border-emerald-300'
                                  : 'bg-white border border-slate-200 hover:border-emerald-400 hover:bg-emerald-50'
                              }`}
                              data-testid={`practice-word-${idx}`}
                            >
                              <div className="flex items-start justify-between">
                                <div>
                                  <p className="font-thai text-lg">{word.thai}</p>
                                  <p className="text-xs text-slate-500">{word.french}</p>
                                </div>
                                {isCompleted && (
                                  <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                                )}
                              </div>
                            </button>
                          );
                        });
                      })()}
                    </div>
                  )}

                  {/* Progression - TOUJOURS exactement 5 mots clés */}
                  {(() => {
                    // RÈGLE ABSOLUE: Toujours 5 mots clés
                    const FIXED_PRACTICE_COUNT = 5;
                    
                    // Vérifier si les 5 mots sont complétés
                    // (on compare avec le nombre de mots réellement affichés, qui est toujours 5)
                    const wordsAvailable = Math.min(
                      (story.words_to_write?.length || 0) + (story.words_used?.length || 0),
                      FIXED_PRACTICE_COUNT
                    );
                    const totalForPractice = Math.min(wordsAvailable, FIXED_PRACTICE_COUNT);
                    
                    return completedWords.length >= totalForPractice && totalForPractice > 0 && (
                      <div className="mt-4 p-4 bg-emerald-100 rounded-xl text-center">
                        <Trophy className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                        <p className="font-medium text-emerald-700">
                          Félicitations ! Tu as maîtrisé les 5 mots clés !
                        </p>
                      </div>
                    );
                  })()}
                    </div>
                  )}
                </div>

                <Button
                  variant="outline"
                  onClick={() => setShowLearningSupport(false)}
                  className="w-full rounded-full"
                >
                  Fermer le support d'apprentissage
                </Button>

                <Button
                  onClick={() => setShowManeeChat(true)}
                  className="w-full rounded-full bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white"
                  data-testid="ask-ploy-btn"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Poser une question à Manee sur cette histoire
                </Button>
              </div>
            )}

            {/* Manee Chat for Story Context */}
            <ManeeChat
              isOpen={showManeeChat}
              onClose={() => setShowManeeChat(false)}
              context={story ? `Histoire en cours d'étude:\n\nThaï: ${story.story_thai}\n\nFrançais: ${story.story_french}\n\nMots utilisés: ${story.words_used.map(w => `${w.thai} (${w.french})`).join(', ')}` : null}
            />
          </div>
        )}

        {/* Close button */}
        <Button
          variant="outline"
          onClick={() => { stopStory(); onClose(); }}
          className="w-full mt-4 rounded-full"
        >
          Fermer
        </Button>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StoryGeneratorModal;
